// ✅ Trekkin.app — PanelGuide.jsx v7.0.0 (EVENT CHAT FIX + SEM ENTER / SEM CONVITES / LIVE SEGURO)
// -----------------------------------------------------------------------------
// ✔ User normal NÃO entra em grupos manualmente (não há botão "Entrar")
// ✔ User normal NÃO ativa LIVE (zero joinGroup automáticos a partir daqui)
// ✔ Chat só aparece se already-member (evento geriu isso)
// ✔ Guia/Admin continuam a gerir grupos
// ✔ Toda a UI preservada
// ✔ Regra de Ouro respeitada (nada essencial removido)
// ✔ FIX v7.0.0:
//    • Antes de abrir o Chat do Grupo, faz joinGroup(g.id)
//      → define currentGroup corretamente
//      → deixa o PanelGroupChat saber qual é o grupo ativo, sem depender do LIVE

import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  ref,
  onValue,
  query,
  limitToLast,
  update,
  remove,
  orderByChild,
  equalTo,
  push,
  get,
  set,
} from "firebase/database";
import { useCan } from "../hooks/useCan";
import { useEffectiveRole } from "../hooks/useEffectiveRole.js";

import { auth, db } from "../firebaseConfig";
import { useAuthStore } from "../stores/useAuthStore";
import { useGroupStore } from "../stores/useGroupStore";
import { useEventsStore } from "../stores/useEventsStore";
import { usePanelsStore } from "../stores/usePanelsStore";
import { logEvent } from "../stores/useLogsStore";
import { useTranslate } from "../i18n/useTranslate";
import { localizeEvent } from "../i18n/localizeEvent.js";
import { createOrGetDirectChat } from "../functions/createOrGetDirectChat.js";
import { useLanguageStore } from "../stores/useLanguageStore";
import { useActivityModeStore } from "../stores/useActivityModeStore.jsx";
import { useMapStore } from "../stores/useMapStore.jsx";
import { getActivityModeLabel, isActivityModeCompatible } from "../utils/activityMode.js";
import { sendCustomBroadcastPush } from "../utils/sendCustomBroadcastPush.js";
import { uploadImageToServer } from "../utils/uploadToServer.js";

import TrekkinButton from "./TrekkinButton";
import CloseButton from "./CloseButton";
import TrekkinConfirm from "./TrekkinConfirm";

const GUIDE_PROFILES_CACHE_KEY = "trekkin.panel.guideProfiles.cache.v1";
const GUIDES_STORE_CACHE_KEY = "trekkin.guides.cache.v1";

function readLastKnownLocation() {
  try {
    const saved = JSON.parse(localStorage.getItem("trekkin_last_location") || "null");
    const lat = Number(saved?.lat ?? saved?.latitude);
    const lon = Number(saved?.lon ?? saved?.lng ?? saved?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
  } catch {
    return null;
  }
}

function waypointDistanceMeters(position, waypoint) {
  const lat1 = Number(position?.lat);
  const lon1 = Number(position?.lon);
  const lat2 = Number(waypoint?.lat ?? waypoint?.latitude);
  const lon2 = Number(waypoint?.lon ?? waypoint?.lng ?? waypoint?.longitude);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const rad = (value) => value * Math.PI / 180;
  const dLat = rad(lat2 - lat1);
  const dLon = rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function sortWaypointsByDistance(list, position) {
  return [...list].sort((a, b) => {
    const delta = waypointDistanceMeters(position, a) - waypointDistanceMeters(position, b);
    return Number.isFinite(delta) && delta !== 0 ? delta : String(a?.name || "").localeCompare(String(b?.name || ""), "pt");
  });
}

function readGuideProfilesCache() {
  try {
    const profileCache = JSON.parse(localStorage.getItem(GUIDE_PROFILES_CACHE_KEY) || "[]");
    const storeCache = JSON.parse(localStorage.getItem(GUIDES_STORE_CACHE_KEY) || "[]");
    const merged = [
      ...(Array.isArray(profileCache) ? profileCache : []),
      ...(Array.isArray(storeCache) ? storeCache : []),
    ];
    const byUid = new Map();
    merged.forEach((guide) => {
      const id = String(guide?.id || guide?.uid || guide?.userId || "").trim();
      if (!id) return;
      byUid.set(id, { ...(byUid.get(id) || {}), ...(guide || {}), id, uid: id });
    });
    return [...byUid.values()];
  } catch {
    return [];
  }
}

function writeGuideProfilesCache(guides) {
  try {
    const safeGuides = Array.isArray(guides) ? guides : [];
    localStorage.setItem(GUIDE_PROFILES_CACHE_KEY, JSON.stringify(safeGuides));
    localStorage.setItem(GUIDES_STORE_CACHE_KEY, JSON.stringify(safeGuides));
  } catch {}
}
import { showToast } from "./NotifyToast.jsx";
import {
  GUIDE_REQUEST_STATES,
  GUIDE_REQUEST_STATE_LABELS,
  normalizeGuideRequestState,
} from "../utils/guideRequestStateMachine.js";

const IMAGE_ACCEPT = "image/*,.heic,.heif,image/heic,image/heif";

const resolveGuideUid = (group, event = null) =>
  String(
    event?.guideUid ||
      event?.guideId ||
      event?.createdByUid ||
      group?.guideUid ||
      group?.guideId ||
      group?.owner_uid ||
      group?.ownerUid ||
      group?.ownerId ||
      group?.createdBy ||
      ""
  ).trim();

const clampRating = (value) => Math.max(1, Math.min(5, Math.round(Number(value) || 0)));

const guideRatingSectionStyle = {
  background: "rgba(255,255,255,0.94)",
  borderRadius: 16,
  padding: 14,
  border: "1px solid rgba(0,0,0,0.07)",
  marginBottom: 8,
};

const guideRatingCardStyle = {
  background: "rgba(40,167,69,0.06)",
  border: "1px solid rgba(40,167,69,0.2)",
  borderRadius: 12,
  padding: 11,
};

const guideRatingInputStyle = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  border: "1px solid rgba(0,0,0,0.18)",
  background: "#fff",
  color: "#202020",
  borderRadius: 10,
  padding: "10px 11px",
  font: "inherit",
  fontSize: 13,
};

const guideRatingPrimaryButtonStyle = {
  border: "none",
  background: "#28a745",
  color: "#fff",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 12,
  fontWeight: 750,
  cursor: "pointer",
};

const guideRatingSecondaryButtonStyle = {
  ...guideRatingPrimaryButtonStyle,
  border: "1px solid rgba(0,0,0,0.18)",
  background: "#fff",
  color: "#202020",
};

const customerProposalButtonStyle = {
  ...guideRatingPrimaryButtonStyle,
  minHeight: 40,
  minWidth: 0,
  width: "100%",
  border: "1px solid #28a745",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const customerProposalViewButtonStyle = {
  ...customerProposalButtonStyle,
  background: "var(--tk-proposal-action-bg, #fff)",
  color: "var(--tk-proposal-action-text, #202020)",
  borderColor: "var(--tk-proposal-action-border, #202020)",
};

const guideRatingModalBackdropStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 10020,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  boxSizing: "border-box",
};

const guideRatingModalStyle = {
  width: "min(460px, 100%)",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  background: "rgba(255,255,255,0.99)",
  borderRadius: 20,
  padding: 18,
  boxShadow: "0 16px 45px rgba(0,0,0,0.25)",
  boxSizing: "border-box",
};

const finiteProposalNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const guideProposalDiffersFromRequest = (request = {}) => {
  const pricing = request.pricing || {};
  const proposal = request.proposalSnapshot || {};
  const comparisons = [
    [finiteProposalNumber(proposal.hours ?? pricing.hours), finiteProposalNumber(request.requestedHours)],
    [finiteProposalNumber(proposal.participants), finiteProposalNumber(request.peopleCount)],
    [finiteProposalNumber(proposal.pricePerHour ?? pricing.pricePerHour), finiteProposalNumber(request?.estimate?.pricePerHourPerPerson)],
    [finiteProposalNumber(proposal.insurancePerPerson ?? pricing.insurancePerPerson), finiteProposalNumber(request?.estimate?.insurancePerPerson)],
    [finiteProposalNumber(proposal.total ?? pricing.total), finiteProposalNumber(request?.estimate?.total)],
  ];
  if (comparisons.some(([proposed, requested]) => proposed !== null && requested !== null && Math.abs(proposed - requested) > 0.005)) return true;

  const proposedStart = proposal.startAt ? new Date(proposal.startAt).getTime() : null;
  const requestedStart = request.startTime ? new Date(request.startTime).getTime() : null;
  if (Number.isFinite(proposedStart) && Number.isFinite(requestedStart) && proposedStart !== requestedStart) return true;
  if (Object.prototype.hasOwnProperty.call(proposal, "routeDescription") && String(proposal.routeDescription || "").trim() !== String(request.routeDescription || "").trim()) return true;
  return pricing.offer === true && finiteProposalNumber(request?.estimate?.total) > 0;
};

function resizeImageToJpeg(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const max = 1600;
      const ratio = Math.min(1, max / Math.max(img.width, img.height));
      const width = Math.round(img.width * ratio);
      const height = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Falha ao preparar imagem."));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Falha ao preparar imagem."));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.85
      );
    };
    img.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    img.src = URL.createObjectURL(file);
  });
}

async function prepareGuideRequestImageFile(file) {
  const jpegBlob = await resizeImageToJpeg(file);
  return new File([jpegBlob], `proposal_${Date.now()}.jpg`, {
    type: "image/jpeg",
  });
}

const toTimestampMs = (value) => {
  if (value == null || value === "") return null;

  if (typeof value === "number") {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (typeof value === "object") {
    if (typeof value.toMillis === "function") {
      return Number(value.toMillis());
    }
    if (typeof value.seconds === "number") {
      const nanos = typeof value.nanoseconds === "number" ? value.nanoseconds : 0;
      return value.seconds * 1000 + Math.round(nanos / 1e6);
    }
    if (value instanceof Date) {
      return value.valueOf();
    }
  }

  return null;
};

const normalizeStartAt = (ev) => {
  if (!ev?.startAt) return null;
  return toTimestampMs(ev.startAt);
};

const parseTimestampValue = (value) => {
  return toTimestampMs(value);
};

const formatDateTime = (value, language) => {
  if (value == null || value === "") return "";
  const ts = toTimestampMs(value);
  if (Number.isFinite(ts)) {
    try {
      return new Date(ts).toLocaleString(language === "en" ? "en-GB" : "pt-PT");
    } catch {
      return new Date(ts).toString();
    }
  }
  return String(value);
};

const getGroupTimestamp = (group, eventStartById) => {
  if (!group) return 0;
  const eventId = group?.activeEventId || group?.eventId || group?.groupId || null;
  const fromEvent = eventId ? eventStartById.get(eventId) : null;
  if (fromEvent) return fromEvent;

  const candidates = [
    group?.activeEvent?.startAt,
    group?.activeEvent?.date,
    group?.eventData?.startAt,
    group?.eventData?.startTs,
    group?.eventData?.eventDate,
    group?.eventData?.date,
    group?.eventStartAt,
    group?.eventStartTs,
    group?.startAt,
    group?.startTs,
    group?.eventDate,
  ];

  for (const value of candidates) {
    const timestamp = parseTimestampValue(value);
    if (timestamp) return timestamp;
  }

  return 0;
};

const getEndOfDayTs = (ts) => {
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  try {
    const d = new Date(ts);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  } catch {
    return 0;
  }
};

const getEventEndTs = (event) => {
  if (!event) return 0;

  const explicitEnd = [event.endAt, event.endDate, event.finishedAt]
    .map(parseTimestampValue)
    .filter((value) => Number.isFinite(value) && value > 0);
  if (explicitEnd.length) return Math.max(...explicitEnd);

  const dayTimestamps = (Array.isArray(event.meetingPoints) ? event.meetingPoints : [])
    .map((day) => parseTimestampValue(day?.date || day?.startAt))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (dayTimestamps.length) return getEndOfDayTs(Math.max(...dayTimestamps));

  return getEndOfDayTs(normalizeStartAt(event) || 0);
};

const isPaidConfirmedEventRegistration = (registration) => {
  if (!registration) return false;
  const registrationStatus = String(registration.status || "").trim().toLowerCase();
  const paymentStatus = String(registration.payment?.status || registration.paymentStatus || "")
    .trim()
    .toLowerCase();
  return registrationStatus === "confirmed" && paymentStatus === "paid";
};

const getEventImageUrl = (event) => {
  if (!event) return null;
  return (
    event.imageUrl ||
    event.teaserUrl ||
    event.coverImage ||
    event.photoUrl ||
    event.trailImage ||
    event.image ||
    null
  );
};

const parseGuestEmails = (raw) => {
  if (!raw) return [];
  return String(raw)
    .split(/[,;\n]/g)
    .map((s) => s.trim())
    .filter(Boolean);
};

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));

const formatCurrency = (value, currency = "EUR", lang = "pt") => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return "—";
  try {
    return new Intl.NumberFormat(lang === "en" ? "en-GB" : "pt-PT", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const GUIDE_ONLINE_DRAFT_KEY = "guideOnlineDraft";

const resolveGuestUidsByEmail = async (emails = []) => {
  const uniqueEmails = Array.from(
    new Set(
      emails
        .map((email) => String(email || "").trim().toLowerCase())
        .filter(Boolean)
    )
  );

  if (uniqueEmails.length === 0) {
    return { matchedUids: [], unmatchedEmails: [] };
  }

  const usersSnap = await get(ref(db, "users"));
  const users = usersSnap.val() || {};
  const emailToUid = {};

  Object.entries(users).forEach(([uid, data]) => {
    const email = String(data?.email || "").trim().toLowerCase();
    if (email) emailToUid[email] = uid;
  });

  const matchedUids = [];
  const unmatchedEmails = [];

  uniqueEmails.forEach((email) => {
    const uid = emailToUid[email];
    if (uid) matchedUids.push(uid);
    else unmatchedEmails.push(email);
  });

  return {
    matchedUids: Array.from(new Set(matchedUids)),
    unmatchedEmails,
  };
};

export default function PanelGuide(props) {
  const panelProps = props || {};
  const { user } = useAuthStore();
  const translate = useTranslate();
  const base = import.meta.env.BASE_URL || "/";
  const language = useLanguageStore((state) => state.language);
  const activityMode = useActivityModeStore((state) => state.activityMode);
  const { effectiveRole: role } = useEffectiveRole();

  const {
    groups,
    currentGroup,
    listenGroups,
    stopListeningGroups,
    createGroup,
    deleteGroup,
    joinGroup, // 🆕 usado só para definir currentGroup antes do chat
  } = useGroupStore();

  const { resetToMap, openPanel } = usePanelsStore();
  const can = useCan();

  // ------------------------------
  // LISTAS SEGURAS
  // ------------------------------

  const safeAllGroups = (Array.isArray(groups) ? groups : []).filter((group) => {
    if (["admin", "guide"].includes(role)) return true;
    return group?.eventPublished !== false || !!group?.members?.[user?.uid];
  });
  const list = can("PanelGuide", "view_groups") ? safeAllGroups : [];
  const events = useEventsStore((s) => s.events || []);
  const userEvents = useEventsStore((s) => s.userEvents || {});
  const isLoadingEvents = useEventsStore((s) => s.isLoadingEvents === true);

  const eventStartById = useMemo(() => {
    const map = new Map();
    events.forEach((ev) => {
      const ts = normalizeStartAt(ev);
      if (ts) map.set(ev.id, ts);
    });
    return map;
  }, [events]);

  const eventsById = useMemo(() => {
    const map = new Map();
    events.forEach((ev) => {
      if (ev?.id) map.set(ev.id, ev);
    });
    return map;
  }, [events]);

  const guideRequestHeroImage = useMemo(() => {
    const nowTs = Date.now();
    const candidates = events
      .map((event) => ({
        event,
        image: getEventImageUrl(event),
        startAt: normalizeStartAt(event) || Number.MAX_SAFE_INTEGER,
      }))
      .filter((entry) => entry.image)
      .sort((a, b) => {
        const aUpcoming = a.startAt >= nowTs ? 0 : 1;
        const bUpcoming = b.startAt >= nowTs ? 0 : 1;
        return aUpcoming - bUpcoming || a.startAt - b.startAt;
      });
    return candidates[0]?.image || `${import.meta.env.BASE_URL || "/"}topo-map.png`;
  }, [events]);

  const eventsByGroupId = useMemo(() => {
    const map = new Map();
    events.forEach((event) => {
      const groupId = String(event?.groupId || "").trim();
      if (!groupId) return;
      const current = map.get(groupId);
      const eventTs = normalizeStartAt(event) || toTimestampMs(event?.updatedAt) || 0;
      const currentTs = normalizeStartAt(current) || toTimestampMs(current?.updatedAt) || 0;
      if (!current || eventTs >= currentTs) map.set(groupId, event);
    });
    return map;
  }, [events]);

  const groupsById = useMemo(() => {
    const map = new Map();
    safeAllGroups.forEach((g) => {
      if (g?.id) map.set(g.id, g);
    });
    return map;
  }, [safeAllGroups]);

  const [newGroupName, setNewGroupName] = useState("");

  // ------------------------------
  // GUIA ONLINE — PEDIDOS
  // ------------------------------
  const [showOnlineRequest, setShowOnlineRequest] = useState(false);
  const [guideOnlineConfig, setGuideOnlineConfig] = useState({
    insurancePerPerson: 0,
    pricePerHourPerPerson: 0,
    currency: "EUR",
  });
  const [trailProposalConfig, setTrailProposalConfig] = useState({
    averageWalkingSpeedKmh: 4,
    pricePerKm: 0,
    accompanimentPricePerHour: 0,
    minimumDailyPrice: 0,
    extraPersonPrice: 0,
    currency: "EUR",
  });
  const [onlineRequest, setOnlineRequest] = useState({
    requestKind: "guide_online",
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phoneNumber || "",
    peopleCount: 1,
    requestedHours: 2,
    requestedKm: 10,
    requestedDays: 1,
    overnightRequired: false,
    overnightType: "camping",
    startTime: "",
    meetingPoint: null,
    proposalWaypointName: "",
    routeDescription: "",
    proposalImageUrl: "",
    guestEmails: "",
    guestParticipants: [],
    includeInsurance: false,
    includeOffroadTour: false,
  });
  const [proposalImageUploading, setProposalImageUploading] = useState(false);
  const [onlineSubmitting, setOnlineSubmitting] = useState(false);
  const [userOnlineRequests, setUserOnlineRequests] = useState([]);
  const [pendingOnlineRequests, setPendingOnlineRequests] = useState([]);
  const [managedOnlineRequests, setManagedOnlineRequests] = useState([]);
  const [acceptingRequest, setAcceptingRequest] = useState(null);
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [payingRequestId, setPayingRequestId] = useState(null);
  const [notifyingRequestId, setNotifyingRequestId] = useState(null);
  const [acceptForm, setAcceptForm] = useState({
    hours: 2,
    pricePerHour: 0,
    insurancePerPerson: 0,
    offer: false,
  });
  const acceptTotal = useMemo(() => {
    if (!acceptingRequest) return 0;
    if (String(acceptingRequest.type || "guide_online") === "trail_proposal") {
      return acceptForm.offer ? 0 : Math.max(0, Number(acceptingRequest?.estimate?.total || 0));
    }
    const people = Math.max(1, Number(acceptingRequest.peopleCount || 1));
    const hours = Math.max(1, Number(acceptForm.hours || 0));
    const pricePerHour = Number(acceptForm.pricePerHour || 0);
    const insurance = Number(acceptForm.insurancePerPerson || 0);
    if (acceptForm.offer) return 0;
    return people * (insurance + pricePerHour * hours);
  }, [
    acceptingRequest,
    acceptForm.hours,
    acceptForm.pricePerHour,
    acceptForm.insurancePerPerson,
    acceptForm.offer,
  ]);
  const userGuideOnlineRequests = useMemo(
    () =>
      (userOnlineRequests || []).filter(
        (req) =>
          isActivityModeCompatible(req?.activityMode, activityMode) &&
          String(req?.type || "guide_online") !== "trail_proposal"
      ),
    [userOnlineRequests, activityMode]
  );
  const userTrailProposalRequests = useMemo(
    () =>
      (userOnlineRequests || []).filter(
        (req) =>
          isActivityModeCompatible(req?.activityMode, activityMode) &&
          String(req?.type || "guide_online") === "trail_proposal"
      ),
    [userOnlineRequests, activityMode]
  );
  const pendingGuideOnlineRequests = useMemo(
    () =>
      (pendingOnlineRequests || []).filter(
        (req) =>
          isActivityModeCompatible(req?.activityMode, activityMode) &&
          String(req?.type || "guide_online") !== "trail_proposal"
      ),
    [pendingOnlineRequests, activityMode]
  );
  const pendingTrailProposalRequests = useMemo(
    () =>
      (pendingOnlineRequests || []).filter(
        (req) =>
          isActivityModeCompatible(req?.activityMode, activityMode) &&
          String(req?.type || "guide_online") === "trail_proposal"
      ),
    [pendingOnlineRequests, activityMode]
  );
  const acceptedManagedTrailProposals = useMemo(
    () =>
      (managedOnlineRequests || []).filter((req) => {
        if (!isActivityModeCompatible(req?.activityMode, activityMode)) return false;
        const linkedEventId = String(req?.eventId || "").trim();
        const linkedEvent = linkedEventId ? eventsById.get(linkedEventId) : null;
        if (linkedEvent) {
          const eventStatus = String(linkedEvent?.status || "").trim().toLowerCase();
          const eventEndTs = getEventEndTs(linkedEvent);
          if (eventStatus === "finished" || eventStatus === "completed" || linkedEvent?.finishedAt || (eventEndTs > 0 && eventEndTs < Date.now())) return false;
        }
        const state = normalizeGuideRequestState(req?.status, req);
        const isAccepted = ![
          GUIDE_REQUEST_STATES.REQUESTED,
          GUIDE_REQUEST_STATES.REJECTED,
          GUIDE_REQUEST_STATES.CANCELLED,
          GUIDE_REQUEST_STATES.EXPIRED,
        ].includes(state);
        return isAccepted;
      }),
    [managedOnlineRequests, activityMode, eventsById]
  );

  const [directChats, setDirectChats] = useState([]);
  const [groupLastTs, setGroupLastTs] = useState({});
  const [guides, setGuides] = useState(() => readGuideProfilesCache());
  const [showAllGuides, setShowAllGuides] = useState(false);
  const [guideActivityModes, setGuideActivityModes] = useState({});
  const [aboutGuide, setAboutGuide] = useState(null);
  useEffect(() => {
    const requestedGuideId = String(panelProps?.aboutGuideId || "").trim();
    if (!requestedGuideId || !guides.length) return;
    const requestedGuide = guides.find((guide) =>
      [guide?.id, guide?.uid, guide?.userId].some((value) => String(value || "") === requestedGuideId)
    );
    if (requestedGuide) setAboutGuide(requestedGuide);
  }, [guides, panelProps?.aboutGuideId]);
  const [featuredWaypoints, setFeaturedWaypoints] = useState([]);
  const [featuredSlide, setFeaturedSlide] = useState(0);
  const [showFeaturedWaypoints, setShowFeaturedWaypoints] = useState(false);
  const [featuredPreview, setFeaturedPreview] = useState(null);
  const [partnerWaypoints, setPartnerWaypoints] = useState([]);
  const [partnerSlide, setPartnerSlide] = useState(0);
  const [showPartnerWaypoints, setShowPartnerWaypoints] = useState(false);
  const [showBicycleAssistance, setShowBicycleAssistance] = useState(false);
  const [showMyRequests, setShowMyRequests] = useState(false);
  const [showDirectChatHistory, setShowDirectChatHistory] = useState(false);
  const [partnerBrandPreview, setPartnerBrandPreview] = useState(null);
  const [bicycleAssistanceWaypoints, setBicycleAssistanceWaypoints] = useState([]);
  const mapUserLocation = useMapStore((state) => state.userLocation);
  const [partnerSortPosition, setPartnerSortPosition] = useState(readLastKnownLocation);
  const [bicycleAssistanceSlide, setBicycleAssistanceSlide] = useState(0);

  useEffect(() => {
    const lat = Number(mapUserLocation?.lat ?? mapUserLocation?.latitude);
    const lon = Number(mapUserLocation?.lon ?? mapUserLocation?.lng ?? mapUserLocation?.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      setPartnerSortPosition({ lat, lon });
    }
  }, [mapUserLocation]);

  useEffect(() => {
    const handleNativeLocation = (event) => {
      const detail = event?.detail || {};
      const lat = Number(detail.lat ?? detail.latitude);
      const lon = Number(detail.lon ?? detail.lng ?? detail.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

      const nextLocation = { lat, lon };
      setPartnerSortPosition(nextLocation);
      try {
        localStorage.setItem("trekkin_last_location", JSON.stringify({
          ...nextLocation,
          accuracy: Number(detail.acc ?? detail.accuracy) || null,
          timestamp: Number(detail.ts) || Date.now(),
        }));
      } catch {}
    };

    window.addEventListener("native:location", handleNativeLocation);
    return () => window.removeEventListener("native:location", handleNativeLocation);
  }, []);
  const [guideRatings, setGuideRatings] = useState({});
  const [guideRatingResponses, setGuideRatingResponses] = useState({});
  const [guideRatingAcknowledgements, setGuideRatingAcknowledgements] = useState({});
  const [guideRatingModeration, setGuideRatingModeration] = useState({});
  const [ratingTarget, setRatingTarget] = useState(null);
  const [ratingDraft, setRatingDraft] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [savingGuideRating, setSavingGuideRating] = useState(false);
  const [responseDrafts, setResponseDrafts] = useState({});
  const [savingResponseId, setSavingResponseId] = useState("");
  const [confirmingRatingId, setConfirmingRatingId] = useState("");

  const now = Date.now();

  const groupsWithMeta = useMemo(() => {
    return list
      .map((group) => {
        const eventId = group?.activeEventId || group?.eventId || null;
        const legacyEventId = group?.groupId || null;
        const event =
          (eventId ? eventsById.get(eventId) : null) ||
          eventsByGroupId.get(String(group?.id || "")) ||
          (legacyEventId ? eventsById.get(legacyEventId) : null) ||
          null;
        const ts = normalizeStartAt(event) || getGroupTimestamp(group, eventStartById);
        const sortTs = parseTimestampValue(group?.createdAt) || ts || 0;
        return {
          group,
          event,
          ts,
          sortTs,
          dayEndTs: getEndOfDayTs(ts),
        };
      })
      .sort((a, b) => b.sortTs - a.sortTs);
  }, [list, eventStartById, eventsByGroupId, eventsById]);

  const futureGroups = groupsWithMeta
    .filter(({ ts, dayEndTs }) => ts && dayEndTs >= now)
    .sort((a, b) => b.sortTs - a.sortTs);
  const pastGroups = groupsWithMeta
    .filter(({ ts, dayEndTs }) => !ts || dayEndTs < now)
    .sort((a, b) => b.sortTs - a.sortTs);

  const ratingSummaryByGuide = useMemo(() => {
    const summaries = new Map();
    Object.entries(guideRatings || {}).forEach(([guideUid, guideNode]) => {
      const reviews = Object.entries(guideNode?.reviews || {})
        .map(([id, value]) => ({ id, ...(value || {}) }))
        .filter((review) => Number(review?.stars) >= 1 && Number(review?.stars) <= 5)
        .filter((review) => {
          const status = String(guideRatingModeration?.[guideUid]?.[review.id] || "published");
          return status === "published";
        });
      const total = reviews.reduce((sum, review) => sum + Number(review.stars), 0);
      summaries.set(guideUid, {
        reviews,
        count: reviews.length,
        average: reviews.length ? total / reviews.length : 0,
      });
    });
    return summaries;
  }, [guideRatingModeration, guideRatings]);

  const availableGuides = useMemo(() => {
    const guideName = (guide) =>
      String(
        guide?.name ||
          guide?.displayName ||
          guide?.username ||
          translate("common.unknown")
      ).trim();

    return guides
      .filter((guide) =>
        isActivityModeCompatible(
          guideActivityModes?.[guide.id]?.mode || guide?.guideActivityMode,
          activityMode
        )
      )
      .slice()
      .sort((a, b) => {
        const aSummary = ratingSummaryByGuide.get(String(a?.id || a?.uid || ""));
        const bSummary = ratingSummaryByGuide.get(String(b?.id || b?.uid || ""));
        const aHasRatings = Number(aSummary?.count || 0) > 0;
        const bHasRatings = Number(bSummary?.count || 0) > 0;

        if (aHasRatings !== bHasRatings) return aHasRatings ? -1 : 1;
        if (aHasRatings && bHasRatings) {
          const ratingDifference = Number(bSummary.average || 0) - Number(aSummary.average || 0);
          if (Math.abs(ratingDifference) > 0.0001) return ratingDifference;
        }

        return guideName(a).localeCompare(guideName(b), language === "en" ? "en" : "pt-PT", {
          sensitivity: "base",
          numeric: true,
        });
      });
  }, [activityMode, guideActivityModes, guides, language, ratingSummaryByGuide, translate]);

  const eligibleGuideRatings = useMemo(() => {
    if (!user?.uid) return [];
    const seen = new Set();
    return Object.entries(userEvents || {})
      .map(([eventId, registration]) => {
        const normalizedEventId = String(eventId || "").trim();
        const event = eventsById.get(normalizedEventId);
        if (!event || !isPaidConfirmedEventRegistration(registration)) return null;

        const eventEndTs = getEventEndTs(event);
        if (!eventEndTs || eventEndTs >= now) return null;

        const group = groupsById.get(String(event?.groupId || "")) || null;
        const guideUid = resolveGuideUid(group, event);
        const groupId = String(group?.id || event?.groupId || "").trim();
        if (!guideUid || guideUid === user.uid || !normalizedEventId) return null;
        const key = `${normalizedEventId}_${user.uid}`;
        if (seen.has(key)) return null;
        seen.add(key);
        const guide = guides.find((item) => String(item?.id) === guideUid);
        return {
          key,
          eventId: normalizedEventId,
          groupId,
          eventName: event?.name || event?.title || group?.name || "Evento",
          eventAt: normalizeStartAt(event) || eventEndTs,
          guideUid,
          guideName:
            guide?.name || guide?.displayName || group?.guideName || group?.owner_name || "Guia",
          existing: guideRatings?.[guideUid]?.reviews?.[key] || null,
        };
      })
      .filter(Boolean);
  }, [eventsById, groupsById, guides, guideRatings, now, user?.uid, userEvents]);

  const ownGuideRatingSummary = user?.uid
    ? ratingSummaryByGuide.get(user.uid) || { reviews: [], count: 0, average: 0 }
    : { reviews: [], count: 0, average: 0 };

  const latestOwnGuideRatings = useMemo(
    () =>
      ownGuideRatingSummary.reviews
        .filter((review) => !guideRatingAcknowledgements?.[user?.uid]?.[review.id])
        .slice()
        .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0)),
    [guideRatingAcknowledgements, ownGuideRatingSummary.reviews, user?.uid]
  );

  const aboutGuideRatingSummary = aboutGuide?.id
    ? ratingSummaryByGuide.get(String(aboutGuide.id)) || { reviews: [], count: 0, average: 0 }
    : { reviews: [], count: 0, average: 0 };

  const pendingGuideRatings = useMemo(
    () => eligibleGuideRatings
      .filter((target) => !target.existing)
      .sort((a, b) => Number(b.eventAt || 0) - Number(a.eventAt || 0))
      .slice(0, 3),
    [eligibleGuideRatings]
  );

  const openGuideRating = (target) => {
    const existing = target?.existing || null;
    setRatingTarget(target);
    setRatingDraft(existing ? clampRating(existing.stars) : 0);
    setRatingComment(String(existing?.comment || ""));
  };

  const saveGuideRating = async () => {
    if (!user?.uid || !ratingTarget?.guideUid || !ratingTarget?.eventId) return;
    if (ratingDraft < 1 || ratingDraft > 5) {
      showToast("Escolhe uma pontuação de 1 a 5 estrelas.", "warning");
      return;
    }
    setSavingGuideRating(true);
    try {
      const reviewId = `${ratingTarget.eventId}_${user.uid}`;
      await set(
        ref(db, `guideRatings/${ratingTarget.guideUid}/reviews/${reviewId}`),
        {
          stars: clampRating(ratingDraft),
          comment: String(ratingComment || "").trim().slice(0, 1000),
          reviewerUid: user.uid,
          reviewerName: user.name || user.displayName || "Participante",
          reviewerPhoto: user.photoURL || user.avatar || null,
          eventId: ratingTarget.eventId,
          groupId: ratingTarget.groupId || "",
          eventName: ratingTarget.eventName || "Evento",
          eventAt: Number(ratingTarget.eventAt || 0),
          guideUid: ratingTarget.guideUid,
          updatedAt: Date.now(),
          createdAt: Number(ratingTarget?.existing?.createdAt || Date.now()),
        }
      );
      if (!ratingTarget?.existing) {
        try {
          await push(ref(db, "alerts/criticos"), {
            type: "nova_avaliacao_guia",
            label: "Nova avaliação de guia",
            detail: `${clampRating(ratingDraft)} estrelas`,
            note: `${user.name || user.displayName || "Participante"} avaliou ${ratingTarget.guideName || "um guia"} no evento "${ratingTarget.eventName || "Evento"}".`,
            userId: user.uid,
            userName: user.name || user.displayName || "Participante",
            guideUid: ratingTarget.guideUid,
            guideName: ratingTarget.guideName || "Guia",
            eventId: ratingTarget.eventId,
            eventName: ratingTarget.eventName || "Evento",
            reviewId,
            stars: clampRating(ratingDraft),
            ts: Date.now(),
            adminOnly: true,
            persistent: true,
            source: "guide_rating",
          });
        } catch (alertError) {
          console.warn("Avaliação guardada, mas falhou o alerta administrativo:", alertError);
        }
      }
      showToast("Avaliação do guia guardada.", "success");
      setRatingTarget(null);
    } catch (error) {
      console.warn("Falha ao guardar avaliação do guia:", error);
      const denied = String(error?.code || error?.message || "").toLowerCase().includes("permission");
      showToast(
        denied
          ? "A avaliação só pode ser guardada por participantes deste evento."
          : "Não foi possível guardar a avaliação.",
        "error"
      );
    } finally {
      setSavingGuideRating(false);
    }
  };

  const saveGuideResponse = async (review) => {
    if (!user?.uid || !review?.id) return;
    const response = String(responseDrafts[review.id] || "").trim().slice(0, 1000);
    if (!response) {
      showToast("Escreve uma resposta antes de guardar.", "warning");
      return;
    }
    setSavingResponseId(review.id);
    try {
      await set(ref(db, `guideRatingResponses/${user.uid}/${review.id}`), {
        response,
        guideUid: user.uid,
        updatedAt: Date.now(),
      });
      setResponseDrafts((current) => ({ ...current, [review.id]: "" }));
      showToast("Resposta publicada.", "success");
    } catch (error) {
      console.warn("Falha ao responder à avaliação:", error);
      showToast("Não foi possível publicar a resposta.", "error");
    } finally {
      setSavingResponseId("");
    }
  };

  const confirmGuideRating = async (review) => {
    if (!user?.uid || !review?.id) return;
    setConfirmingRatingId(review.id);
    try {
      await set(ref(db, `guideRatingAcknowledgements/${user.uid}/${review.id}`), {
        guideUid: user.uid,
        confirmedAt: Date.now(),
      });
      showToast("Avaliação confirmada.", "success");
    } catch (error) {
      console.warn("Falha ao confirmar avaliação:", error);
      showToast("Não foi possível confirmar a avaliação.", "error");
    } finally {
      setConfirmingRatingId("");
    }
  };

  // ------------------------------
  // CHAT DIRETO COM GUIA
  // ------------------------------

  // ------------------------------
  // GUIAS REGISTADOS (ROLE GUIDE)
  // ------------------------------

  useEffect(() => {
    if (!user?.uid) return;
    const stopRatings = onValue(ref(db, "guideRatings"), (snap) => {
      setGuideRatings(snap.val() || {});
    });
    const stopResponses = onValue(ref(db, "guideRatingResponses"), (snap) => {
      setGuideRatingResponses(snap.val() || {});
    });
    const stopAcknowledgements = onValue(
      ref(db, "guideRatingAcknowledgements"),
      (snap) => setGuideRatingAcknowledgements(snap.val() || {})
    );
    const stopModeration = onValue(ref(db, "guideRatingVisibility"), (snap) => {
      setGuideRatingModeration(snap.val() || {});
    });
    return () => {
      stopRatings();
      stopResponses();
      stopAcknowledgements();
      stopModeration();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    return onValue(ref(db, "config/trailProposal"), (snap) => {
      const cfg = snap.val() || {};
      setTrailProposalConfig((prev) => ({
        ...prev,
        averageWalkingSpeedKmh: Number(cfg.averageWalkingSpeedKmh ?? prev.averageWalkingSpeedKmh) || 4,
        pricePerKm: Number(cfg.pricePerKm ?? prev.pricePerKm) || 0,
        accompanimentPricePerHour: Number(cfg.accompanimentPricePerHour ?? prev.accompanimentPricePerHour) || 0,
        minimumDailyPrice: Number(cfg.minimumDailyPrice ?? prev.minimumDailyPrice) || 0,
        extraPersonPrice: Number(cfg.extraPersonPrice ?? prev.extraPersonPrice) || 0,
        currency: cfg.currency || prev.currency || "EUR",
      }));
    });
  }, [user?.uid]);

  // ------------------------------
  // LOG ABERTURA
  // ------------------------------

  useEffect(() => {
    if (!user) return;
    logEvent("system", "PanelGuide aberto", {
      uid: user.uid,
      role: user.role || "free",
    });
  }, [user]);

  // ------------------------------
  // LISTENER GRUPOS
  // ------------------------------

  useEffect(() => {
    listenGroups?.();
    logEvent("system", "PanelGuide listeners iniciados");
    return () => logEvent("system", "PanelGuide desmontado");
  }, [listenGroups]);

  // ------------------------------
  // LISTENER CHAT DE GRUPO (última msg por grupo)
  // ------------------------------
  useEffect(() => {
    if (!list.length) {
      setGroupLastTs({});
      return;
    }

    const unsubs = [];
    list.forEach((g) => {
      if (!g?.id) return;
      const q = query(ref(db, `groupChat/${g.id}/messages`), limitToLast(1));
      const unsub = onValue(q, (snap) => {
        const v = snap.val() || {};
        const arr = Object.values(v);
        const last = arr.length
          ? Math.max(...arr.map((m) => m.ts || m.timestamp || 0))
          : 0;
        setGroupLastTs((prev) =>
          prev[g.id] === last ? prev : { ...prev, [g.id]: last }
        );
      });
      unsubs.push(unsub);
    });

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [list]);

  const isGroupUnread = (gid) => {
    const readKey = `groupChat.${gid}.lastRead`;
    const readRaw = localStorage.getItem(readKey);
    const lastTs = groupLastTs[gid] || 0;
    let lastRead = Number(readRaw || 0);
    if (!readRaw && lastTs > 0) {
      lastRead = lastTs;
      try {
        localStorage.setItem(readKey, String(lastRead));
      } catch {}
    }
    return lastTs > lastRead;
  };

  // ------------------------------
  // GUIA ONLINE — CONFIG + PEDIDOS
  // ------------------------------
  useEffect(() => {
    if (!user?.uid) return;
    const cfgRef = ref(db, "config/guideOnline");
    return onValue(cfgRef, (snap) => {
      const cfg = snap.val() || {};
      setGuideOnlineConfig((prev) => ({
        ...prev,
        insurancePerPerson:
          Number(cfg.insurancePerPerson ?? prev.insurancePerPerson ?? 0) || 0,
        pricePerHourPerPerson:
          Number(cfg.pricePerHourPerPerson ?? prev.pricePerHourPerPerson ?? 0) || 0,
        currency: cfg.currency || prev.currency || "EUR",
      }));
      setAcceptForm((prev) => ({
        ...prev,
        pricePerHour:
          Number(cfg.pricePerHourPerPerson ?? prev.pricePerHour ?? 0) || 0,
        insurancePerPerson:
          Number(cfg.insurancePerPerson ?? prev.insurancePerPerson ?? 0) || 0,
      }));
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user) return;
    setOnlineRequest((prev) => ({
      ...prev,
      name: user?.name || prev.name,
      email: user?.email || prev.email,
      phone: user?.phoneNumber || prev.phone,
    }));
  }, [user?.uid, user?.name, user?.email, user?.phoneNumber]);

  useEffect(() => {
    const shouldRestore =
      panelProps?.openOnlineRequest ||
      panelProps?.prefillOnlineRequest ||
      panelProps?.callbackResult ||
      (Number.isFinite(panelProps?.lat) && Number.isFinite(panelProps?.lon));

    if (!shouldRestore) return;

    let draft = null;
    try {
      const raw = sessionStorage.getItem(GUIDE_ONLINE_DRAFT_KEY);
      if (raw) draft = JSON.parse(raw);
    } catch {}

    if (draft && typeof draft === "object") {
      setOnlineRequest((prev) => ({ ...prev, ...draft }));
    }
    if (
      panelProps?.prefillOnlineRequest &&
      typeof panelProps.prefillOnlineRequest === "object"
    ) {
      setOnlineRequest((prev) => ({
        ...prev,
        ...panelProps.prefillOnlineRequest,
      }));
    }

    const lat = panelProps?.callbackResult?.lat ?? panelProps?.lat;
    const lon = panelProps?.callbackResult?.lon ?? panelProps?.lon;
    const callbackPlace =
      panelProps?.callbackResult?.placeName || panelProps?.placeName || null;
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      const resolvedName =
        callbackPlace ||
        draft?.meetingPoint?.placeName ||
        translate("guide.online.status.placeFallback");
      setOnlineRequest((prev) => ({
        ...prev,
        meetingPoint: { lat, lon, placeName: resolvedName },
      }));
    }

    setShowOnlineRequest(true);
  }, [
    panelProps?.openOnlineRequest,
    panelProps?.prefillOnlineRequest,
    panelProps?.callbackResult?.lat,
    panelProps?.callbackResult?.lon,
    panelProps?.lat,
    panelProps?.lon,
  ]);

  useEffect(() => {
    if (!user?.uid) return;
    const base = ref(db, "guideOnlineRequests");
    const ownQuery = query(base, orderByChild("ownerUid"), equalTo(user.uid));
    const unsubOwn = onValue(ownQuery, (snap) => {
      const v = snap.val() || {};
      const list = Object.entries(v).map(([id, data]) => ({ id, ...(data || {}) }));
      setUserOnlineRequests(list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    });

    let unsubPending = () => {};
    let unsubManaged = () => {};
    if (["guide", "admin"].includes(role)) {
      unsubPending = onValue(base, (snap) => {
        const v = snap.val() || {};
        const list = Object.entries(v)
          .map(([id, data]) => ({ id, ...(data || {}) }))
          .filter(
            (request) =>
              normalizeGuideRequestState(request.status, request) ===
              GUIDE_REQUEST_STATES.REQUESTED
          );
        setPendingOnlineRequests(list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      });

      if (role === "admin") {
        unsubManaged = onValue(base, (snap) => {
          const v = snap.val() || {};
          const list = Object.entries(v).map(([id, data]) => ({ id, ...(data || {}) }));
          setManagedOnlineRequests(
            list.sort((a, b) => (b.acceptedAt || b.createdAt || 0) - (a.acceptedAt || a.createdAt || 0))
          );
        });
      } else {
        const managedQuery = query(base, orderByChild("guideUid"), equalTo(user.uid));
        unsubManaged = onValue(managedQuery, (snap) => {
          const v = snap.val() || {};
          const list = Object.entries(v).map(([id, data]) => ({ id, ...(data || {}) }));
          setManagedOnlineRequests(
            list.sort((a, b) => (b.acceptedAt || b.createdAt || 0) - (a.acceptedAt || a.createdAt || 0))
          );
        });
      }
    } else {
      setPendingOnlineRequests([]);
      setManagedOnlineRequests([]);
    }

    return () => {
      unsubOwn();
      unsubPending();
      unsubManaged();
    };
  }, [user?.uid, role]);

  useEffect(() => {
    const r = ref(db, "waypoints");
    const unsub = onValue(
      r,
      (snap) => {
        const raw = snap.val() || {};
        const list = Object.entries(raw)
          .map(([id, wp]) => ({ id, ...(wp || {}) }))
          .filter((wp) => wp?.featuredProposal === true)
          .filter((wp) => String(wp?.visibilityScope || "public") !== "private" || ["guide", "admin"].includes(role))
          .filter((wp) => String(wp?.imageUrl || "").trim() !== "")
          .sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));
        setFeaturedWaypoints(list);
        const accommodationWaypoints = sortWaypointsByDistance(Object.entries(raw)
            .map(([id, wp]) => ({ id, ...(wp || {}) }))
            .filter((wp) => wp?.accommodationPartner === true)
            .filter((wp) => wp?.partnerVisible === true || role === "admin")
            .filter((wp) => String(wp?.visibilityScope || "public") !== "private" || ["guide", "admin"].includes(role))
            .filter((wp) => String(wp?.imageUrl || "").trim() !== ""), partnerSortPosition);
        const groupedAccommodation = [];
        const brandIndexes = new Map();
        accommodationWaypoints.forEach((wp) => {
          const brandId = String(wp?.accommodationBrandId || "").trim();
          if (!brandId) {
            groupedAccommodation.push({ ...wp, waypoints: [wp], isBrandGroup: false });
            return;
          }
          if (!brandIndexes.has(brandId)) {
            brandIndexes.set(brandId, groupedAccommodation.length);
            groupedAccommodation.push({
              ...wp,
              id: `brand-${brandId}`,
              name: wp.accommodationBrandName || wp.name,
              imageUrl: wp.accommodationBrandLogoUrl || wp.imageUrl,
              waypoints: [wp],
              isBrandGroup: true,
            });
          } else {
            groupedAccommodation[brandIndexes.get(brandId)].waypoints.push(wp);
          }
        });
        setPartnerWaypoints(groupedAccommodation);
        setBicycleAssistanceWaypoints(
          sortWaypointsByDistance(Object.entries(raw)
            .map(([id, wp]) => ({ id, ...(wp || {}) }))
            .filter((wp) => wp?.assistancePartner === true)
            .filter((wp) => String(wp?.partnerUserUid || "").trim() !== "")
            .filter((wp) => String(wp?.visibilityScope || "public") !== "private" || ["guide", "admin"].includes(role))
            , partnerSortPosition)
        );
        setFeaturedSlide((slide) => Math.min(slide, Math.max(0, Math.ceil(list.length / 4) - 1)));
        setPartnerSlide((slide) => Math.min(slide, Math.max(0, Math.ceil(groupedAccommodation.length / 4) - 1)));
        setBicycleAssistanceSlide((slide) => Math.min(slide, Math.max(0, Math.ceil(Object.values(raw).filter((wp) => wp?.assistancePartner === true && String(wp?.partnerUserUid || "").trim() !== "" && (String(wp?.visibilityScope || "public") !== "private" || ["guide", "admin"].includes(role))).length / 4) - 1)));
      },
      () => {
        setFeaturedWaypoints([]);
      }
    );
    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [role, partnerSortPosition]);

  useEffect(() => onValue(ref(db, "guideActivityModes"), (snapshot) => {
    setGuideActivityModes(snapshot.val() || {});
  }), []);

  // ------------------------------
  // LISTENER CHATS DIRETOS (GUIA)
  // ------------------------------
  const canDirectChat = can("PanelGuide", "direct_chat");

  useEffect(() => {
    const cached = readGuideProfilesCache();
    if (cached.length) setGuides(cached);
    // Os cartões dos guias usam apenas o perfil público. A leitura de `/users`
    // falha para utilizadores normais e fazia desaparecer toda a lista.
    const r = ref(db, "usersPublic");
    const unsub = onValue(
      r,
      (snap) => {
        const data = snap.val() || {};
        const arr = Object.entries(data)
          // Mantém sempre o UID real da chave do RTDB.
          // Alguns perfis podem ter campo "id" interno e sobrescrever o contacto alvo.
          .map(([id, u]) => ({ ...(u || {}), id }))
          .filter((u) => {
            if (!u?.id) return false;
            const role = String(u.role || "").toLowerCase();
            return role === "guide" || role === "admin";
          })
          .sort((a, b) =>
            String(a.name || a.displayName || a.username || "").localeCompare(
              String(b.name || b.displayName || b.username || ""),
              "pt"
            )
          );
        if (arr.length) {
          setGuides(arr);
          writeGuideProfilesCache(arr);
        } else {
          const fallback = readGuideProfilesCache();
          if (fallback.length) setGuides(fallback);
        }
      },
      (err) => {
        console.warn("⚠️ Erro a carregar guias:", err);
        const fallback = readGuideProfilesCache();
        if (fallback.length) setGuides(fallback);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    if (!canDirectChat) return;

    const inboxPaths = role === "admin"
      ? ["guideDirectChats", "userDirectChats"]
      : role === "guide"
        ? ["guideDirectChats"]
        : ["userDirectChats"];
    const inboxValues = Object.fromEntries(inboxPaths.map((path) => [path, {}]));
    let refreshId = 0;
    let disposed = false;

    const refreshChats = async () => {
      const currentRefreshId = ++refreshId;
      const merged = new Map();
      inboxPaths.forEach((inboxPath) => {
        Object.entries(inboxValues[inboxPath] || {}).forEach(([chatId, data]) => {
          const candidate = { chatId, ...(data || {}), inboxPath };
          const previous = merged.get(chatId);
          if (!previous || Number(candidate.lastMessageAt || 0) >= Number(previous.lastMessageAt || 0)) {
            merged.set(chatId, candidate);
          }
        });
      });

      let arr = Array.from(merged.values())
        .filter((c) => !!c.chatId && !c.archived && !c.ended);

      // Resolver o outro participante e ocultar entradas vazias/órfãs.
      arr = await Promise.all(
        arr.map(async (c) => {
          try {
            const chatSnap = await get(ref(db, `directChats/${c.chatId}`));

            const chat = chatSnap.val();
            if (!chatSnap.exists()) return null;
            const contactUid = String(chat?.userId || "") === String(user.uid)
              ? chat?.guideId
              : chat?.userId;
            if (!contactUid) return null;
            if (chat?.ended === true) return null;
            const lastTs = Number(chat?.lastMessageAt || c?.lastMessageAt || 0);
            if (!lastTs) return null;

            const userSnap = await get(ref(db, `users/${contactUid}`));
            let u = userSnap.val();
            if (!u) {
              const publicSnap = await get(ref(db, `usersPublic/${contactUid}`));
              u = publicSnap.val();
            }

            if (!u) {
              return {
                ...c,
                userId: chat.userId,
                guideId: chat.guideId,
                contactUid,
                contactName: c.userName || c.guideName || "Utilizador",
                contactPhoto: null,
                lastMessageAt: lastTs,
              };
            }

            return {
              ...c,
              userId: chat.userId,
              guideId: chat.guideId,
              contactUid,
              contactName: u.name || u.displayName || u.username || "Utilizador",
              contactPhoto: u.photoURL || u.avatar || u.photoUrl || null,
              lastMessageAt: lastTs,
            };
          } catch {
            return null;
          }
        })
      );
      arr = arr.filter(Boolean).filter((c) => Number(c?.lastMessageAt || 0) > 0);
      arr.sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));
      if (!disposed && currentRefreshId === refreshId) setDirectChats(arr);
    };

    const unsubs = inboxPaths.map((inboxPath) =>
      onValue(ref(db, `${inboxPath}/${user.uid}`), (snap) => {
        inboxValues[inboxPath] = snap.val() || {};
        refreshChats();
      })
    );

    return () => {
      disposed = true;
      unsubs.forEach((unsub) => unsub());
    };
  }, [user?.uid, role, canDirectChat]);

  // ------------------------------
  // VERIFICAR MEMBRO REAL (evento decide)
  // ------------------------------

  const isMember = (g) => {
    const uid = user?.uid;
    return !!uid && !!g.members?.[uid];
  };

  // ------------------------------
  // CRIAR GRUPO (apenas guias/admin)
  // ------------------------------

  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return alert(translate("guide.alert.groupNameRequired"));

    try {
      const gid = await createGroup(name);

      logEvent("system", "Grupo criado", { gid, name });

      setNewGroupName("");
    } catch (e) {
      logEvent("error", "Erro ao criar grupo", { error: e?.message });
      alert(translate("guide.alert.createGroupFail"));
    }
  };

  // ------------------------------
  // GUIA ONLINE — FORM
  // ------------------------------

  const openOnlineRequest = () => {
    if (!user?.uid) {
      showToast(translate("guide.online.toast.authRequired"));
      return;
    }
    setShowOnlineRequest(true);
  };

  const clearOnlineDraft = () => {
    try {
      sessionStorage.removeItem(GUIDE_ONLINE_DRAFT_KEY);
    } catch {}
  };

  const closeOnlineRequestModal = () => {
    setShowOnlineRequest(false);
    clearOnlineDraft();
    setOnlineRequest((prev) => ({
      ...prev,
      proposalWaypointName: "",
      routeDescription: "",
      proposalImageUrl: "",
      guestEmails: "",
      guestParticipants: [],
      includeInsurance: false,
      includeOffroadTour: false,
    }));
  };

  const handleProposalImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        showToast("A imagem requer ligação à internet.", "error");
        return;
      }
      setProposalImageUploading(true);
      const prepared = await prepareGuideRequestImageFile(file);
      const url = await uploadImageToServer(prepared);
      setOnlineRequest((prev) => ({ ...prev, proposalImageUrl: String(url || "") }));
      showToast("Imagem anexada ao pedido.", "success");
    } catch (err) {
      showToast(String(err?.message || "Falha ao enviar imagem."), "error");
    } finally {
      setProposalImageUploading(false);
      if (event?.target) event.target.value = "";
    }
  };

  const handleSubmitOnlineRequest = async () => {
    if (!user?.uid) return;
    const requestKind =
      String(onlineRequest.requestKind || "guide_online") === "trail_proposal"
        ? "trail_proposal"
        : "guide_online";
    const name = onlineRequest.name?.trim();
    const email = onlineRequest.email?.trim();
    const phone = onlineRequest.phone?.trim();
    const routeDescription = onlineRequest.routeDescription?.trim();
    const proposalWaypointName = String(
      onlineRequest.proposalWaypointName || ""
    ).trim();
    const peopleCount = Number(onlineRequest.peopleCount || 0);
    const requestedHours = Number(onlineRequest.requestedHours || 0);
    const requestedKm = Number(onlineRequest.requestedKm || 0);
    const requestedDays = Math.max(1, Number(onlineRequest.requestedDays || 1));
    const startTime = String(onlineRequest.startTime || "").trim();
    const includeInsurance =
      requestKind === "trail_proposal" && !!onlineRequest.includeInsurance;
    const includeOffroadTour =
      requestKind === "trail_proposal" && !!onlineRequest.includeOffroadTour;

    if (
      !name ||
      !email ||
      !phone ||
      !routeDescription ||
      peopleCount <= 0 ||
      (requestKind === "guide_online" ? requestedHours <= 0 : requestedKm <= 0) ||
      !startTime
    ) {
      showToast(translate("guide.online.toast.missingFields"));
      return;
    }

    const guestParticipantsRaw = Array.isArray(onlineRequest.guestParticipants)
      ? onlineRequest.guestParticipants
      : [];
    const guestParticipants = guestParticipantsRaw
      .map((p) => ({
        name: String(p?.name || "").trim(),
        email: normalizeEmail(String(p?.email || "")),
      }))
      .filter((p) => isValidEmail(p.email));
    const guestEmails = guestParticipants.length
      ? guestParticipants.map((p) => p.email)
      : parseGuestEmails(onlineRequest.guestEmails);

    setOnlineSubmitting(true);
    try {
      const base = ref(db, "guideOnlineRequests");
      const newRef = push(base);
      const payload = {
        type: requestKind,
        activityMode,
        status: GUIDE_REQUEST_STATES.REQUESTED,
        stateVersion: 1,
        createdAt: Date.now(),
        ownerUid: user.uid,
        ownerName: name,
        ownerEmail: email,
        ownerPhone: phone,
        peopleCount,
        requestedHours,
        requestedKm: requestKind === "trail_proposal" ? requestedKm : null,
        requestedDays: requestKind === "trail_proposal" ? requestedDays : null,
        overnightRequired:
          requestKind === "trail_proposal" ? !!onlineRequest.overnightRequired : false,
        overnightType:
          requestKind === "trail_proposal" && onlineRequest.overnightRequired
            ? String(onlineRequest.overnightType || "camping")
            : null,
        startTime,
        meetingPoint: null,
        proposalWaypointName: proposalWaypointName || null,
        routeDescription,
        proposalImageUrl:
          String(onlineRequest.proposalImageUrl || "").trim() || null,
        guestEmails,
        guestParticipants: guestParticipants.length ? guestParticipants : null,
        includeInsurance,
        includeOffroadTour,
        estimate:
          requestKind === "guide_online"
            ? null
            : (() => {
                const speed = Math.max(0.1, Number(trailProposalConfig.averageWalkingSpeedKmh || 4));
                const walkingHours = requestedKm / speed;
                const routePrice =
                  requestedKm * Number(trailProposalConfig.pricePerKm || 0) +
                  walkingHours * Number(trailProposalConfig.accompanimentPricePerHour || 0);
                const basePrice = Math.max(
                  routePrice,
                  requestedDays * Number(trailProposalConfig.minimumDailyPrice || 0)
                );
                const extraPeopleTotal =
                  Math.max(0, peopleCount - 1) * Number(trailProposalConfig.extraPersonPrice || 0);
                return {
                  total: basePrice + extraPeopleTotal,
                  basePrice,
                  extraPeopleTotal,
                  extraPersonPrice: Number(trailProposalConfig.extraPersonPrice || 0),
                  walkingHours,
                  currency: trailProposalConfig.currency || "EUR",
                };
              })(),
      };
      await set(newRef, payload);
      try {
        await push(ref(db, "alerts/criticos"), {
          type: "admin_guide_request",
          sub:
            requestKind === "trail_proposal"
              ? "novo_pedido_trilho_personalizado"
              : "novo_pedido_guia_online",
          label:
            requestKind === "trail_proposal"
              ? "Novo pedido: Trilho personalizado"
              : "Novo pedido: Guia On-Line",
          message: `${name} • ${peopleCount} participante(s)`,
          ts: Date.now(),
          persistent: true,
          adminOnly: true,
          requestId: newRef.key || null,
          requestType: requestKind,
          ownerUid: user.uid,
          ownerName: name,
          ownerEmail: email,
          userId: user.uid,
          userName: name,
          detail: `${name} • ${peopleCount} participante(s)`,
        });
      } catch (alertErr) {
        console.warn("guideOnline admin alert fail:", alertErr);
      }
      showToast(translate("guide.online.toast.sent"));
      setShowOnlineRequest(false);
      clearOnlineDraft();
      setOnlineRequest((prev) => ({
        ...prev,
        proposalWaypointName: "",
        requestKind: prev.requestKind || "guide_online",
        routeDescription: "",
        proposalImageUrl: "",
        guestEmails: "",
        guestParticipants: [],
        includeInsurance: false,
        includeOffroadTour: false,
      }));
    } catch (err) {
      console.warn("guideOnline request fail:", err);
      showToast(translate("guide.online.toast.error"));
    } finally {
      setOnlineSubmitting(false);
    }
  };

  const openAcceptRequest = async (
    req,
    initialOffer = null,
    openEventEditor = false
  ) => {
    if (!req) return;
    const reqType =
      String(req?.type || "guide_online") === "trail_proposal"
        ? "trail_proposal"
        : "guide_online";
    const currentState = normalizeGuideRequestState(req.status, req);
    if (["trail_proposal", "guide_online"].includes(reqType) || openEventEditor) {
      // Uma proposta de trilho corresponde sempre a um único evento privado.
      // O evento pode não estar na lista visível (por ser privado), portanto o
      // eventId do pedido é suficiente para abrir a edição.
      let existingEventId = String(req?.eventId || "").trim();
      if (!existingEventId && req?.id) {
        try {
          const existingEventsSnap = await get(
            query(ref(db, "events"), orderByChild("proposalRequestId"), equalTo(req.id))
          );
          const existingEvents = existingEventsSnap.val() || {};
          existingEventId = Object.keys(existingEvents)[0] || "";
          if (existingEventId) {
            await update(ref(db, `guideOnlineRequests/${req.id}`), {
              eventId: existingEventId,
              eventName: existingEvents[existingEventId]?.name || req?.eventName || null,
              updatedAt: Date.now(),
            });
          }
        } catch (lookupError) {
          console.warn("Falha ao procurar proposta existente:", lookupError);
        }
      }
      if (existingEventId) {
        openPanel("eventEdit", { id: existingEventId, eventId: existingEventId });
        return;
      }
      try {
        const assignedAt = Date.now();
        await update(ref(db, `guideOnlineRequests/${req.id}`), {
          status:
            currentState === GUIDE_REQUEST_STATES.REQUESTED
              ? GUIDE_REQUEST_STATES.GUIDE_ASSIGNED
              : currentState,
          guideUid: user?.uid || null,
          guideName: user?.name || user?.displayName || user?.email || null,
          guideEmail: user?.email || null,
          assignedAt,
          updatedAt: assignedAt,
        });
      } catch (error) {
        console.warn("Falha ao atribuir proposta ao guia:", error);
        showToast("Não foi possível assumir este pedido.", "error");
        return;
      }
      const rawStartAt = req?.startTime;
      const parsedStartAt =
        typeof rawStartAt === "number"
          ? rawStartAt
          : Date.parse(String(rawStartAt || ""));
      const prefillStartAt = Number.isFinite(parsedStartAt)
        ? new Date(parsedStartAt)
        : null;
      const pad = (n) => String(n).padStart(2, "0");
      const prefillDate = prefillStartAt
        ? `${prefillStartAt.getFullYear()}-${pad(prefillStartAt.getMonth() + 1)}-${pad(
            prefillStartAt.getDate()
          )}`
        : "";
      const prefillTime = prefillStartAt
        ? `${pad(prefillStartAt.getHours())}:${pad(prefillStartAt.getMinutes())}`
        : "";
      const proposalName =
        reqType === "trail_proposal"
          ? "Trilho Personalizado Trekkin"
          : "Guia On-Line";
      openPanel("eventCreate", {
        prefill: {
          proposalRequestId: req?.id || null,
          proposalBaseParticipants: Number(req?.peopleCount || 0) || null,
          proposalPricingMode: "group_total",
          proposalGuestEmails: Array.isArray(req?.guestEmails)
            ? req.guestEmails
            : parseGuestEmails(req?.guestEmails),
          proposalGuestParticipants: Array.isArray(req?.guestParticipants)
            ? req.guestParticipants
            : [],
          name: proposalName,
          // A descrição escrita pelo cliente pertence ao pedido privado e não
          // deve ser reutilizada como conteúdo editorial público do evento.
          shortDescription: "",
          longDescription: "",
          technicalDetails:
            reqType === "trail_proposal"
              ? `${Number(req?.requestedKm || 0)} km · ${Number(req?.requestedDays || 1)} dia(s)${req?.overnightRequired ? ` · Pernoita: ${req?.overnightType === "lodging" ? "alojamento" : "campismo"}` : " · Sem pernoita"}`
              : req?.requestedHours
                ? `Duração solicitada: ${req.requestedHours} hora(s).`
                : "",
          includedInfo: "",
          startLocation: req?.meetingPoint || null,
          meetingPoints: req?.meetingPoint
            ? [{ location: req.meetingPoint, time: prefillTime }]
            : [],
          date: prefillDate,
          time: prefillTime,
          maxParticipants: String(req?.peopleCount || ""),
          imageUrl:
            reqType === "guide_online"
              ? `${import.meta.env.BASE_URL || "/"}assets/guia-online-event-cover.png`
              : `${import.meta.env.BASE_URL || "/"}assets/trilho-personalizado-event-cover.png`,
          price: Number.isFinite(Number(req?.pricing?.total))
            ? String(req.pricing.total)
            : "",
          inviteOnly: true,
          published: false,
          invitedUsers: [
            req?.ownerUid,
            ...(Array.isArray(req?.invitedUids) ? req.invitedUids : []),
          ].reduce((acc, uid) => {
            if (uid) acc[String(uid)] = true;
            return acc;
          }, {}),
        },
      });
      showToast("A abrir o evento em modo proposta.");
      return;
    }
    if (currentState === GUIDE_REQUEST_STATES.REQUESTED) {
      try {
        const assignedAt = Date.now();
        await update(ref(db, `guideOnlineRequests/${req.id}`), {
          status: GUIDE_REQUEST_STATES.GUIDE_ASSIGNED,
          guideUid: user?.uid || null,
          guideName: user?.name || user?.displayName || user?.email || null,
          guideEmail: user?.email || null,
          assignedAt,
          updatedAt: assignedAt,
        });
      } catch (error) {
        console.warn("Falha ao atribuir pedido ao guia:", error);
        showToast("Não foi possível assumir este pedido.", "error");
        return;
      }
    }
    setRejectingRequest(null);
    setAcceptingRequest({
      ...req,
      status:
        currentState === GUIDE_REQUEST_STATES.REQUESTED
          ? GUIDE_REQUEST_STATES.GUIDE_ASSIGNED
          : currentState,
      guideUid: user?.uid || null,
    });
    setAcceptForm({
      hours: Number(req?.pricing?.hours ?? req.requestedHours ?? 2),
      pricePerHour: Number(
        req?.pricing?.pricePerHour ?? guideOnlineConfig.pricePerHourPerPerson ?? 0
      ),
      insurancePerPerson: Number(
        req?.pricing?.insurancePerPerson ?? guideOnlineConfig.insurancePerPerson ?? 0
      ),
      offer:
        initialOffer === null
          ? !!req?.pricing?.offer
          : !!initialOffer,
    });
  };

  const openRejectRequest = async (req) => {
    if (!req) return;
    if (
      normalizeGuideRequestState(req.status, req) === GUIDE_REQUEST_STATES.REQUESTED &&
      role === "guide"
    ) {
      try {
        const assignedAt = Date.now();
        await update(ref(db, `guideOnlineRequests/${req.id}`), {
          status: GUIDE_REQUEST_STATES.GUIDE_ASSIGNED,
          guideUid: user.uid,
          guideName: user?.name || user?.displayName || user?.email || null,
          guideEmail: user?.email || null,
          assignedAt,
          updatedAt: assignedAt,
        });
        req = { ...req, status: GUIDE_REQUEST_STATES.GUIDE_ASSIGNED, guideUid: user.uid };
      } catch (error) {
        showToast("Não foi possível assumir este pedido.", "error");
        return;
      }
    }
    setAcceptingRequest(null);
    setRejectReason("");
    setRejectingRequest(req);
  };

  const handleAcceptRequest = async () => {
    if (!acceptingRequest) return;
    setAcceptingRequest((prev) => ({ ...prev, _saving: true }));
    try {
      const people = Math.max(1, Number(acceptingRequest.peopleCount || 1));
      const hours = Math.max(1, Number(acceptForm.hours || 0));
      const pricePerHour = Number(acceptForm.pricePerHour || 0);
      const insurance = Number(acceptForm.insurancePerPerson || 0);
      const total = acceptForm.offer
        ? 0
        : String(acceptingRequest?.type || "guide_online") === "trail_proposal"
          ? Math.max(0, Number(acceptingRequest?.estimate?.total || 0))
          : people * (insurance + pricePerHour * hours);

      const rawGuestEmails = Array.isArray(acceptingRequest.guestEmails)
        ? acceptingRequest.guestEmails
        : parseGuestEmails(acceptingRequest.guestEmails);
      const { matchedUids, unmatchedEmails } =
        await resolveGuestUidsByEmail(rawGuestEmails);
      const invitedUids = Array.from(
        new Set([acceptingRequest.ownerUid, ...matchedUids].filter(Boolean))
      );
      const ownerUid = String(acceptingRequest.ownerUid || "");
      const guideUid = String(user?.uid || "");
      const draftVisibleUids = Array.from(
        new Set([ownerUid, guideUid, ...invitedUids].filter(Boolean))
      );

      // O grupo definitivo só é criado depois de a proposta ser aceite e paga.
      const groupId = null;

      let eventId = acceptingRequest.eventId || null;
      let acceptedEventName = String(acceptingRequest?.eventName || "").trim();
      if (!eventId) {
        const startAtRaw = acceptingRequest.startTime;
        const startAtParsed =
          typeof startAtRaw === "number" ? startAtRaw : Date.parse(startAtRaw);
        const startAt = Number.isFinite(startAtParsed)
          ? startAtParsed
          : Date.now();
        const dateLabel = new Date(startAt).toLocaleDateString(
          language === "en" ? "en-GB" : "pt-PT"
        );
      const requestType =
          String(acceptingRequest?.type || "guide_online") === "trail_proposal"
            ? "trail_proposal"
            : "guide_online";
      const eventPrefix = translate("guide.online.event.namePrefix");
        const waypointHint = String(acceptingRequest?.proposalWaypointName || "").trim();
        const eventName = requestType === "trail_proposal"
          ? "Trilho Personalizado Trekkin"
          : waypointHint
            ? `${eventPrefix} - ${waypointHint} - ${dateLabel}`
            : `${eventPrefix} ${dateLabel}`;
        acceptedEventName = eventName;

        eventId = push(ref(db, "/events")).key;
        const now = Date.now();
        const guideName =
          user?.name || user?.displayName || user?.email || "Guia";

        // Enquanto é rascunho, o evento pertence apenas ao guia/admin. O
        // cliente só entra em invitedUsers através de "Notificar cliente".
        const invitedUsers = {};

        const eventData = {
          name: eventName,
          activityMode: acceptingRequest?.activityMode === "bike" ? "bike" : "walk",
          // O texto livre do cliente permanece apenas em guideOnlineRequests.
          // O guia escreve separadamente o resumo público do evento.
          description: "",
          shortDescription: null,
          longDescription: null,
          includedInfo: null,
          teaserUrl: null,
          extraImages: null,
          price: 0,
          maxParticipants: null,
          transferPrice: 0,
          transferSeats: 0,
          distanceKm: 0,
          elevationGain: 0,
          startAt,
          endAt: null,
          startLocation: acceptingRequest.meetingPoint || null,
          paymentDeadlineHours: 48,
          createdAt: now,
          createdByUid: user?.uid || null,
          createdByName: guideName,
          createdByEmail: user?.email || null,
          groupId: null,
          status: "open",
          isLive: false,
          liveStartedAt: null,
          liveFinishedAt: null,
          imageUrl:
            requestType === "guide_online"
              ? `${import.meta.env.BASE_URL || "/"}assets/guia-online-event-cover.png`
              : `${import.meta.env.BASE_URL || "/"}assets/trilho-personalizado-event-cover.png`,
          routeGPXUrl: null,
          globalLodgingType: null,
          globalLodgingName: null,
          globalLodgingPrice: null,
          globalLodgings: null,
          meetingPoints: acceptingRequest.meetingPoint
            ? [{ location: acceptingRequest.meetingPoint }]
            : null,
          guideId: user?.uid || null,
          guideName,
          guidePhotoUrl: user?.photoURL || null,
          coGuideId: null,
          coGuideName: null,
          coGuidePhotoUrl: null,
          // A proposta fica disponível apenas no circuito privado. Publicar
          // no site é sempre uma ação manual e separada do fluxo de aceitação.
          published: false,
          isPublic: false,
          inviteOnly: true,
          visibilityMode: "proposal_private",
          proposalRequestType: requestType,
          paymentRequired: !acceptForm.offer,
          paymentStatus: acceptForm.offer ? "not_required" : "pending",
          invitedUsers,
          photos: null,
          alternativeRoutes: null,
          tourRoutes: null,
        };

        const eventUpdates = {
          [`events/${eventId}`]: eventData,
          [`userEvents/${user?.uid}/${eventId}`]: {
            role: "guide",
            status: "owner",
            createdAt: now,
          },
          [`eventsPublic/${eventId}`]: null,
        };

        await update(ref(db), eventUpdates);
      }

      // Quando o guia altera as condições de uma proposta já criada, o pedido e
      // o evento têm de permanecer sincronizados. Antes, apenas o pedido mudava
      // e o cliente continuava a visualizar os valores antigos no evento.
      if (eventId) {
        const proposalEventUpdates = {
          [`events/${eventId}/price`]: total,
          [`events/${eventId}/proposalGroupTotalPrice`]: total,
          [`events/${eventId}/proposalBaseParticipants`]: people,
          [`events/${eventId}/maxParticipants`]: people,
          [`events/${eventId}/technicalDetails`]:
            String(acceptingRequest?.type || "guide_online") === "trail_proposal"
              ? `${Number(acceptingRequest.requestedKm || 0)} km · ${Number(acceptingRequest.requestedDays || 1)} dia(s)${acceptingRequest.overnightRequired ? ` · Pernoita: ${acceptingRequest.overnightType === "lodging" ? "alojamento" : "campismo"}` : ""}`
              : `Duração proposta: ${hours} hora(s).`,
          [`events/${eventId}/includedInfo`]: acceptingRequest.includeInsurance
            ? `Seguro incluído — ${formatCurrency(
                insurance,
                guideOnlineConfig.currency || "EUR",
                language
              )} por pessoa.`
            : "Seguro não incluído.",
          [`events/${eventId}/paymentRequired`]: !acceptForm.offer && total > 0,
          [`events/${eventId}/paymentStatus`]:
            acceptForm.offer || total <= 0 ? "not_required" : "pending",
          [`events/${eventId}/proposalRequestId`]: acceptingRequest.id,
          [`events/${eventId}/updatedAt`]: Date.now(),
        };
        await update(ref(db), proposalEventUpdates);
      }

      await update(ref(db, `guideOnlineRequests/${acceptingRequest.id}`), {
        status: GUIDE_REQUEST_STATES.PROPOSAL_DRAFT,
        acceptedAt: Date.now(),
        guideUid: user?.uid || null,
        guideName: user?.name || user?.displayName || user?.email || null,
        guideEmail: user?.email || null,
        groupId: groupId || null,
        eventId: eventId || null,
        eventName: acceptedEventName || null,
        draftVisibleUids,
        invitedUids,
        pendingGuestEmails: unmatchedEmails.length ? unmatchedEmails : null,
        paymentStatus: acceptForm.offer ? "not_required" : "pending",
        pricing: {
          hours,
          pricePerHour,
          requestedKm: Number(acceptingRequest.requestedKm || 0) || null,
          requestedDays: Number(acceptingRequest.requestedDays || 0) || null,
          extraPersonPrice: Number(acceptingRequest?.estimate?.extraPersonPrice || 0),
          extraPeopleTotal: Number(acceptingRequest?.estimate?.extraPeopleTotal || 0),
          insurancePerPerson: insurance,
          offer: !!acceptForm.offer,
          total,
          currency: guideOnlineConfig.currency || "EUR",
        },
        proposalSnapshot: {
          eventId,
          eventName: acceptedEventName,
          startAt: acceptingRequest.startTime || null,
          meetingPoint: acceptingRequest.meetingPoint || null,
          routeDescription: acceptingRequest.routeDescription || "",
          participants: people,
          guestEmails: rawGuestEmails,
          hours,
          pricePerHour,
          insurancePerPerson: insurance,
          total,
          currency: guideOnlineConfig.currency || "EUR",
        },
        proposalVersion: Math.max(1, Number(acceptingRequest.proposalVersion || 0) + 1),
        proposalSubmittedAt: Date.now(),
        updatedAt: Date.now(),
      });
      showToast("Proposta guardada como rascunho. Usa «Notificar cliente» quando estiver pronta.");
      setAcceptingRequest(null);
    } catch (err) {
      console.warn("accept request fail:", err);
      showToast(translate("guide.online.accept.toast.error"));
      setAcceptingRequest((prev) => (prev ? { ...prev, _saving: false } : prev));
    }
  };

  const handleDeleteOnlineRequest = async (req) => {
    if (!req?.id) return;
    const status = normalizeGuideRequestState(req.status, req);
    const isAdmin = role === "admin";
    const isGuide = role === "guide";
    const isRequestGuide = req?.guideUid && req.guideUid === user?.uid;
    const canDeleteAccepted = isAdmin || (isGuide && isRequestGuide);

    const isActiveProposal = ![
      GUIDE_REQUEST_STATES.REQUESTED,
      GUIDE_REQUEST_STATES.REJECTED,
      GUIDE_REQUEST_STATES.CANCELLED,
      GUIDE_REQUEST_STATES.EXPIRED,
    ].includes(status);

    if (isActiveProposal && !canDeleteAccepted) {
      showToast(translate("guide.online.delete.notAllowed"), "warning");
      return;
    }

    const confirmKey =
      isActiveProposal
        ? "guide.online.delete.confirmAccepted"
        : "guide.online.delete.confirm";
    const ok = window.confirm(translate(confirmKey));
    if (!ok) return;

    try {
      if (isActiveProposal) {
        const eventId = req.eventId || null;
        const groupId = req.groupId || null;
        const event = eventId ? eventsById.get(eventId) : null;

        const invitedUids = new Set(
          Array.isArray(req.invitedUids) ? req.invitedUids : []
        );
        if (event?.invitedUsers && typeof event.invitedUsers === "object") {
          Object.keys(event.invitedUsers || {}).forEach((uid) =>
            invitedUids.add(uid)
          );
        }
        if (req.ownerUid) invitedUids.add(req.ownerUid);
        if (req.guideUid) invitedUids.add(req.guideUid);

        if (eventId) {
          for (const uid of invitedUids) {
            try {
              await remove(ref(db, `eventsInvited/${uid}/${eventId}`));
            } catch (err) {
              console.warn("remove eventsInvited fail:", uid, err);
            }
          }
          try {
            await remove(ref(db, `eventsPublic/${eventId}`));
          } catch {}
          try {
            await remove(ref(db, `eventsLive/${eventId}`));
          } catch {}
          try {
            await remove(ref(db, `eventRegistrations/${eventId}`));
          } catch {}
          await remove(ref(db, `events/${eventId}`));
        }

        if (groupId) {
          try {
            await remove(ref(db, `groupChats/${groupId}`));
          } catch {}
          await remove(ref(db, `groups/${groupId}`));
        }
      }

      await remove(ref(db, `guideOnlineRequests/${req.id}`));
      showToast(translate("guide.online.delete.toast.success"));
    } catch (err) {
      console.warn("delete request fail:", err);
      showToast(translate("guide.online.delete.toast.error"));
    }
  };

  const cleanupAcceptedRequestEvent = async (req) => {
    if (!req?.id) return;
    const eventId = String(req?.eventId || "").trim();
    if (!eventId) {
      showToast("Este pedido não tem evento associado.", "warning");
      return;
    }
    const ok = window.confirm(
      "Eliminar evento associado e manter a proposta aceite?"
    );
    if (!ok) return;

    try {
      const updates = {};
      const invitedUids = new Set(
        Array.isArray(req?.invitedUids) ? req.invitedUids : []
      );
      if (req?.ownerUid) invitedUids.add(req.ownerUid);
      if (req?.guideUid) invitedUids.add(req.guideUid);
      const event = eventsById.get(eventId);
      if (event?.invitedUsers && typeof event.invitedUsers === "object") {
        Object.keys(event.invitedUsers).forEach((uid) => invitedUids.add(uid));
      }

      invitedUids.forEach((uid) => {
        updates[`eventsInvited/${uid}/${eventId}`] = null;
        updates[`userEvents/${uid}/${eventId}`] = null;
      });

      updates[`events/${eventId}`] = null;
      updates[`eventsPublic/${eventId}`] = null;
      updates[`eventsLive/${eventId}`] = null;
      updates[`eventRegistrations/${eventId}`] = null;

      const groupId = String(req?.groupId || "").trim();
      if (groupId) {
        updates[`groups/${groupId}/activeEventId`] = null;
        updates[`groups/${groupId}/guideOnlineEventId`] = null;
      }

      updates[`guideOnlineRequests/${req.id}/eventId`] = null;
      updates[`guideOnlineRequests/${req.id}/eventRemovedAt`] = Date.now();
      updates[`guideOnlineRequests/${req.id}/eventRemovedByUid`] = user?.uid || null;

      await update(ref(db), updates);
      showToast("Evento eliminado. Proposta mantida.");
    } catch (err) {
      console.warn("cleanupAcceptedRequestEvent fail:", err);
      showToast("Falha ao eliminar o evento.", "error");
    }
  };

  const handleDeleteProposalGuide = async (req) => {
    if (!req?.id) return;
    const isGuideOrAdmin = role === "guide" || role === "admin";
    if (!isGuideOrAdmin) {
      showToast("Apenas guia/admin pode eliminar propostas.", "warning");
      return;
    }
    if (req?.eventId) {
      showToast(
        "Este pedido ainda tem evento associado. Elimina primeiro o evento.",
        "warning"
      );
      return;
    }
    const ok = window.confirm("Eliminar proposta definitivamente?");
    if (!ok) return;
    try {
      await remove(ref(db, `guideOnlineRequests/${req.id}`));
      showToast("Proposta eliminada.");
    } catch (err) {
      console.warn("handleDeleteProposalGuide fail:", err);
      showToast("Falha ao eliminar proposta.", "error");
    }
  };

  const handleNotifyProposalReady = async (req) => {
    const requestId = String(req?.id || "").trim();
    const ownerUid = String(req?.ownerUid || "").trim();
    const eventId = String(req?.eventId || "").trim();
    if (!requestId || !ownerUid) {
      showToast("Pedido sem utilizador associado.", "warning");
      return;
    }

    try {
      setNotifyingRequestId(requestId);
      const currentStatus = String(req?.status || "").trim().toLowerCase();
      const alreadyAccepted = [
        GUIDE_REQUEST_STATES.CUSTOMER_ACCEPTED,
        GUIDE_REQUEST_STATES.PAYMENT_PENDING,
        "payment_failed",
      ].includes(currentStatus);
      const ev = eventId ? eventsById.get(eventId) : null;
      const eventName = String(ev?.name || req?.eventName || "").trim();
      const inviteUids = Array.from(
        new Set([
          ownerUid,
          ...(Array.isArray(req?.invitedUids) ? req.invitedUids : []),
        ].filter(Boolean).map((v) => String(v)))
      );

      let eventSnapshot = ev;
      if (eventId && !eventSnapshot) {
        const eventSnap = await get(ref(db, `events/${eventId}`));
        eventSnapshot = eventSnap.val() || null;
      }
      if (!eventId || !eventSnapshot) {
        showToast("Evento da proposta não encontrado.", "warning");
        return;
      }

      if (inviteUids.length > 0) {
        const now = Date.now();
        const updates = {};
        const notifiedEventSnapshot = {
          ...eventSnapshot,
          published: false,
          isPublic: false,
          inviteOnly: true,
          visibilityMode: "proposal_private",
          invitedUsers: {
            ...(eventSnapshot.invitedUsers || {}),
            ...Object.fromEntries(inviteUids.map((inviteUid) => [inviteUid, true])),
          },
        };
        // Uma proposta continua não publicada. O convite explícito é que passa
        // a permitir a visualização no circuito privado dos convidados.
        updates[`events/${eventId}/published`] = false;
        updates[`events/${eventId}/isPublic`] = false;
        updates[`events/${eventId}/inviteOnly`] = true;
        updates[`events/${eventId}/visibilityMode`] = "proposal_private";
        inviteUids.forEach((uid) => {
          updates[`events/${eventId}/invitedUsers/${uid}`] = true;
          updates[`eventsInvited/${uid}/${eventId}`] = notifiedEventSnapshot;
          updates[`userEvents/${uid}/${eventId}`] = {
            role: "participant",
            status: "invited",
            invitedAt: now,
          };
        });
        if (!alreadyAccepted) {
          updates[`guideOnlineRequests/${requestId}/status`] =
            GUIDE_REQUEST_STATES.AWAITING_CUSTOMER;
        }
        updates[`guideOnlineRequests/${requestId}/proposalSnapshot`] = notifiedEventSnapshot;
        const notifiedPrice = Number(
          notifiedEventSnapshot?.proposalGroupTotalPrice > 0
            ? notifiedEventSnapshot.proposalGroupTotalPrice
            : notifiedEventSnapshot?.price || 0
        );
        if (Number.isFinite(notifiedPrice) && notifiedPrice > 0) {
          updates[`guideOnlineRequests/${requestId}/pricing/total`] = notifiedPrice;
          updates[`guideOnlineRequests/${requestId}/pricing/offer`] = false;
          updates[`guideOnlineRequests/${requestId}/paymentStatus`] = "pending";
          updates[`events/${eventId}/proposalGroupTotalPrice`] = notifiedPrice;
          updates[`events/${eventId}/paymentRequired`] = true;
          updates[`events/${eventId}/paymentStatus`] = "pending";
        }
        updates[`guideOnlineRequests/${requestId}/proposalSubmittedAt`] = now;
        updates[`guideOnlineRequests/${requestId}/updatedAt`] = now;
        await update(ref(db), updates);
      }

      const pushResult = await sendCustomBroadcastPush({
        notificationType: alreadyAccepted
          ? "guide_proposal_payment_pending"
          : eventId ? "trail_proposal_ready" : "guide_request_accepted",
        notificationTitle: alreadyAccepted
          ? "Pagamento pendente"
          : eventId ? "Proposta disponível" : "Pedido aceite",
        notificationBody: alreadyAccepted
          ? "A proposta foi aceite. Conclui o pagamento para confirmar a inscrição."
          : eventId
          ? "A tua proposta está pronta. Toca para abrir."
          : "O guia aceitou o teu pedido e está a preparar a proposta.",
        extraData: {
          ...(eventId ? { eventId } : {}),
          ...(eventName ? { eventName } : {}),
          requestId,
          type: alreadyAccepted
            ? "guide_proposal_payment_pending"
            : eventId ? "trail_proposal_ready" : "guide_request_accepted",
        },
        userIds: [ownerUid],
      });
      if (
        !pushResult?.ok ||
        (Number(pushResult?.successCount || 0) < 1 && !pushResult?.inAppAlertCreated)
      ) {
        throw new Error(pushResult?.error || "O dispositivo do cliente não aceitou o push.");
      }

      await update(ref(db, `guideOnlineRequests/${requestId}`), {
        userNotifiedReadyAt: Date.now(),
        userNotifiedReadyByUid: user?.uid || null,
      });

      showToast(
        eventId
          ? "Cliente notificado de que a proposta está disponível."
          : "Cliente notificado de que o pedido foi aceite."
      );
    } catch (err) {
      console.warn("notify proposal ready fail:", err);
      showToast("Falha ao notificar cliente.", "error");
    } finally {
      setNotifyingRequestId(null);
    }
  };

  const handlePayOnlineRequest = async (req) => {
    if (!req?.id || !user?.uid) return;
    if (payingRequestId) return;

    const api = import.meta.env.VITE_API_BASE;
    if (!api) {
      showToast(translate("guide.online.payment.toast.noApi"));
      return;
    }

    try {
      setPayingRequestId(req.id);
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(`${api}/guide-online/create-payment-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken || ""}`,
        },
        body: JSON.stringify({
          requestId: req.id,
          uid: user.uid,
          customerEmail: req.ownerEmail || user.email || null,
        }),
      });

      if (!res.ok) {
        const responseText = await res.text().catch(() => "");
        let payload = {};
        try {
          payload = responseText ? JSON.parse(responseText) : {};
        } catch {
          payload = { error: responseText };
        }
        const paymentError = String(payload?.error || "").trim();
        console.warn("Pagamento guia online falhou:", res.status, paymentError);
        showToast(paymentError || translate("guide.online.payment.toast.error"), "error");
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!data?.paymentReady && !data?.url) {
        showToast(translate("guide.online.payment.toast.error"), "error");
        return;
      }
      if (!data?.paymentEmailSent) throw new Error("Não foi possível enviar o email de pagamento.");
      showToast("Pagamento preparado e enviado por e-mail.", "success");
    } catch (err) {
      console.warn("Pagamento guia online erro:", err);
      showToast(translate("guide.online.payment.toast.error"));
    } finally {
      setPayingRequestId(null);
    }
  };

  const handleCustomerProposalAction = async (req, action) => {
    if (!req?.id || !user?.uid) return;
    const api = import.meta.env.VITE_API_BASE;
    if (!api) {
      showToast("Serviço temporariamente indisponível.", "error");
      return;
    }

    let message = "";
    if (action === "request_changes") {
      message = String(window.prompt("Que alterações pretendes pedir ao guia?") || "").trim();
      if (!message) return;
    }
    if (action === "reject") {
      if (!window.confirm("Rejeitar esta proposta? Esta decisão encerra o pedido.")) return;
      message = String(window.prompt("Motivo da rejeição (opcional):") || "").trim();
    }
    if (action === "accept") {
      const proposalValues = [
        req?.pricing?.total,
        req?.proposalSnapshot?.proposalGroupTotalPrice,
        req?.proposalSnapshot?.price,
      ];
      const explicitProposalTotal = proposalValues.find(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          Number.isFinite(Number(value)) &&
          Number(value) > 0
      ) ?? proposalValues.find((value) => Number.isFinite(Number(value)));
      const freeProposal =
        Number(explicitProposalTotal || 0) <= 0 &&
        (req?.pricing?.offer === true ||
          req?.paymentStatus === "not_required" ||
          Number(explicitProposalTotal || 0) <= 0);
      const question = freeProposal
        ? "Aceitar esta proposta e confirmar o evento privado?"
        : "Aceitar esta proposta e avançar para o pagamento?";
      if (!window.confirm(question)) return;
    }

    try {
      setPayingRequestId(req.id);
      const idToken = await auth.currentUser?.getIdToken();
      const response = await fetch(`${api}/guide-online/action`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken || ""}`,
        },
        body: JSON.stringify({
          requestId: req.id,
          action,
          message,
          proposalVersion: Number(req.proposalVersion || 1),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.error || "Não foi possível atualizar a proposta.");

      if (action === "accept" && result?.paymentRequired) {
        showToast("Proposta aceite. Escolhe a forma de pagamento.", "success");
        setPayingRequestId(null);
        await handlePayOnlineRequest({
          ...req,
          eventId: result?.eventId || req?.eventId,
          status: GUIDE_REQUEST_STATES.CUSTOMER_ACCEPTED,
        });
        return;
      }
      if (action === "accept") showToast("Proposta aceite e evento privado confirmado.", "success");
      if (action === "request_changes") showToast("Pedido de alterações enviado.", "success");
      if (action === "reject") showToast("Proposta rejeitada.", "success");
    } catch (error) {
      console.warn("Falha na decisão da proposta:", error);
      showToast(error?.message || "Não foi possível atualizar a proposta.", "error");
    } finally {
      setPayingRequestId(null);
    }
  };

  const handleViewCustomerProposal = async (req, eventId) => {
    if (!req?.id || !eventId || !user?.uid) return;
    const api = import.meta.env.VITE_API_BASE;
    if (!api) {
      showToast("Serviço temporariamente indisponível.", "error");
      return;
    }
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const response = await fetch(`${api}/guide-online/action`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken || ""}`,
        },
        body: JSON.stringify({
          requestId: req.id,
          action: "view_proposal",
          proposalVersion: Number(req.proposalVersion || 1),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.error || "Não foi possível abrir a proposta.");
      openPanel("eventDetails", { eventId });
    } catch (error) {
      console.warn("Falha ao registar a visualização da proposta:", error);
      showToast(error?.message || "Não foi possível abrir a proposta.", "error");
    }
  };

  const handleRejectRequest = async () => {
    if (!rejectingRequest) return;
    const reason = rejectReason.trim();
    if (!reason) {
      showToast(translate("guide.online.reject.toast.missingReason"));
      return;
    }
    setRejectingRequest((prev) => ({ ...prev, _saving: true }));
    try {
      const requestRef = ref(db, `guideOnlineRequests/${rejectingRequest.id}`);
      const latestSnapshot = await get(requestRef);
      const latestRequest = latestSnapshot.val();
      if (!latestRequest) throw new Error("O pedido já não existe.");

      const latestState = normalizeGuideRequestState(latestRequest.status, latestRequest);
      if (latestState === GUIDE_REQUEST_STATES.REQUESTED && role === "guide") {
        const assignedAt = Date.now();
        await update(requestRef, {
          status: GUIDE_REQUEST_STATES.GUIDE_ASSIGNED,
          guideUid: user.uid,
          guideName: user?.name || user?.displayName || user?.email || null,
          guideEmail: user?.email || null,
          assignedAt,
          updatedAt: assignedAt,
        });
      }

      await update(ref(db, `guideOnlineRequests/${rejectingRequest.id}`), {
        status: GUIDE_REQUEST_STATES.REJECTED,
        rejectedAt: Date.now(),
        rejectedByUid: user?.uid || null,
        rejectedByName: user?.name || user?.displayName || user?.email || null,
        rejectedByEmail: user?.email || null,
        rejectedReason: reason,
      });
      showToast(translate("guide.online.reject.toast.success"));
      setRejectingRequest(null);
    } catch (err) {
      console.warn("reject request fail:", err);
      showToast(translate("guide.online.reject.toast.error"));
      setRejectingRequest((prev) => (prev ? { ...prev, _saving: false } : prev));
    }
  };

  const handleResubmitProposal = async (req) => {
    if (!req?.id || !req?.eventId) return;
    const state = normalizeGuideRequestState(req.status, req);
    if (state !== GUIDE_REQUEST_STATES.CHANGES_REQUESTED) {
      showToast("Esta proposta não tem alterações pendentes.", "warning");
      return;
    }
    try {
      const now = Date.now();
      const eventSnap = await get(ref(db, `events/${req.eventId}`));
      if (!eventSnap.exists()) throw new Error("Evento da proposta não encontrado.");
      const currentVersion = Math.max(1, Number(req.proposalVersion || 1));
      const nextVersion = currentVersion + 1;
      const updates = {
        [`guideOnlineRequests/${req.id}/status`]: GUIDE_REQUEST_STATES.AWAITING_CUSTOMER,
        [`guideOnlineRequests/${req.id}/proposalVersion`]: nextVersion,
        [`guideOnlineRequests/${req.id}/proposalSnapshot`]: eventSnap.val(),
        [`guideOnlineRequests/${req.id}/proposalSubmittedAt`]: now,
        [`guideOnlineRequests/${req.id}/changesResolvedAt`]: now,
        [`guideOnlineRequests/${req.id}/updatedAt`]: now,
      };
      const eventData = eventSnap.val() || {};
      const resubmittedPrice = Number(
        Number(eventData?.proposalGroupTotalPrice) > 0
          ? eventData.proposalGroupTotalPrice
          : eventData?.price || 0
      );
      if (Number.isFinite(resubmittedPrice) && resubmittedPrice > 0) {
        updates[`guideOnlineRequests/${req.id}/pricing/total`] = resubmittedPrice;
        updates[`guideOnlineRequests/${req.id}/pricing/offer`] = false;
        updates[`guideOnlineRequests/${req.id}/paymentStatus`] = "pending";
        updates[`events/${req.eventId}/proposalGroupTotalPrice`] = resubmittedPrice;
        updates[`events/${req.eventId}/paymentRequired`] = true;
        updates[`events/${req.eventId}/paymentStatus`] = "pending";
      }
      if (req.proposalSnapshot) {
        updates[`guideOnlineRequests/${req.id}/proposalHistory/${currentVersion}`] = req.proposalSnapshot;
      }
      await update(ref(db), updates);
      try {
        await sendCustomBroadcastPush({
          notificationType: "guide_proposal_updated",
          notificationTitle: "Proposta atualizada",
          notificationBody: "O guia enviou uma nova versão da tua proposta.",
          extraData: {
            requestId: req.id,
            eventId: req.eventId,
            type: "guide_proposal_updated",
          },
          userIds: [req.ownerUid].filter(Boolean),
        });
      } catch (notificationError) {
        console.warn("Falha ao notificar cliente da nova proposta:", notificationError);
      }
      showToast("Proposta atualizada e reenviada ao cliente.", "success");
    } catch (error) {
      console.warn("Falha ao reenviar proposta:", error);
      showToast("Não foi possível reenviar a proposta.", "error");
    }
  };

  // ------------------------------
  // ABRIR CHAT (só se for membro real)
  // ------------------------------

  const openGroupChat = async (g) => {
    if (!g?.id) return;

    if (!isMember(g)) {
      alert(translate("guide.alert.membersOnly"));
      return;
    }

    try {
      // 🆕 Garante que o store sabe qual é o grupo atual
      await joinGroup({ id: g.id, ...g });
    } catch (e) {
      logEvent("error", "Erro ao fazer joinGroup antes do chat", {
        gid: g.id,
        error: e?.message,
      });
      // Mesmo que o join falhe, não vale a pena abrir o chat sem contexto
      alert(translate("guide.alert.openChatFail"));
      return;
    }

    logEvent("chat", "Abrir chat do grupo", { gid: g.id });
    try {
      localStorage.setItem(`groupChat.${g.id}.lastRead`, String(Date.now()));
    } catch {}
    openPanel("groupChat", { groupId: g.id });
  };

  // ------------------------------
  // ABRIR GESTÃO (guia/admin)
  // ------------------------------

  const openGroupManage = async (g) => {
    if (!g?.id) return;

    // Opcionalmente alinhar currentGroup também aqui
    try {
      await joinGroup({ id: g.id, ...g });
    } catch (e) {
      logEvent("error", "Erro ao fazer joinGroup antes da gestão", {
        gid: g.id,
        error: e?.message,
      });
    }

    logEvent("system", "Abrir gestão do grupo", { gid: g.id });
    openPanel("groupManage", { groupId: g.id });
  };

  // ------------------------------
  // ABRIR CHAT DIRETO (GUIA ↔ USER)
  // ------------------------------

  const openDirectChat = async (chat) => {
    if (!chat?.chatId) return;

    // 🔕 limpar unread no painel
    const inboxPath = chat.inboxPath || (
      ["guide", "admin"].includes(role)
        ? "guideDirectChats"
        : "userDirectChats"
    );
    await update(
      ref(db, `${inboxPath}/${user.uid}/${chat.chatId}`),
      { unread: false }
    );

    logEvent("chat", "Abrir chat direto pelo histórico", {
      chatId: chat.chatId,
      contactUid: chat.contactUid || chat.userId || chat.guideId,
    });

    openPanel("directChat", {
      chatId: chat.chatId,
      source: "guide",
    });
  };

  const openGuideChat = async (guide) => {
    if (!guide?.id || !user?.uid) return;
    if (!canDirectChat) {
      showToast(translate("directChat.alert.planNotAllowed"));
      return;
    }
    try {
      const chatId = await createOrGetDirectChat({
        userId: user.uid,
        guideId: guide.id,
      });
      openPanel("directChat", { chatId, source: "guide-list" });
    } catch (e) {
      logEvent("error", "Erro ao abrir chat com guia", {
        guideId: guide?.id,
        error: e?.message,
      });
      alert(translate("directChat.alert.startFail"));
    }
  };

  const openRequestOwnerDirectChat = async (req) => {
    const ownerUid = String(req?.ownerUid || "").trim();
    const guideUid = String(user?.uid || "").trim();
    const isGuideOrAdmin = role === "guide" || role === "admin";
    if (!ownerUid || !guideUid) return;
    if (!isGuideOrAdmin && !canDirectChat) {
      showToast(translate("directChat.alert.planNotAllowed"));
      return;
    }
    try {
      const chatId = await createOrGetDirectChat({
        userId: ownerUid,
        guideId: guideUid,
        eventId: String(req?.eventId || "").trim() || null,
      });
      try {
        const chatMetaSnap = await get(ref(db, `directChats/${chatId}`));
        const chatMeta = chatMetaSnap.val() || {};
        if (!chatMeta?.proposalWelcomeSentAt) {
          const welcomeText =
            "Recebemos o teu pedido, estamos a trabalhar nele, assim que esteja pronto serás notificado!";
          const now = Date.now();
          await push(ref(db, `directMessages/${chatId}`), {
            from: guideUid,
            text: welcomeText,
            ts: now,
            type: "direct",
          });
          await update(ref(db, `directChats/${chatId}`), {
            lastMessage: welcomeText,
            lastMessageAt: now,
            unread: true,
            proposalWelcomeSentAt: now,
            proposalWelcomeSentBy: guideUid,
          });
          await update(ref(db, `userDirectChats/${ownerUid}/${chatId}`), {
            chatId,
            guideId: guideUid,
            unread: true,
            lastMessageAt: now,
          });
          await update(ref(db, `guideDirectChats/${guideUid}/${chatId}`), {
            chatId,
            userId: ownerUid,
            unread: false,
            lastMessageAt: now,
          });
        }
      } catch (welcomeErr) {
        console.warn("proposal direct chat welcome fail:", welcomeErr);
      }
      openPanel("directChat", { chatId, source: "guide-request" });
    } catch (e) {
      logEvent("error", "Erro ao abrir direct chat do pedido", {
        requestId: req?.id || null,
        ownerUid,
        error: e?.message,
      });
      showToast("Não foi possível abrir o direct chat.", "error");
    }
  };

  const openGuideAbout = (guide) => {
    if (!guide?.id) return;
    setAboutGuide(guide);
  };

  const openTrailProposalFromWaypoint = (wp, options = {}) => {
    const deferOpenMs = Number(options?.deferOpenMs || 0);
    const wpName = String(wp?.name || "").trim();
    setOnlineRequest((prev) => ({
      ...prev,
      requestKind: "trail_proposal",
      proposalWaypointName: wpName || "",
      routeDescription: wpName ? `Interesse principal: ${wpName}` : "",
    }));
    if (deferOpenMs > 0) {
      window.setTimeout(() => openOnlineRequest(), deferOpenMs);
      return;
    }
    openOnlineRequest();
  };

  // ------------------------------
  // DELETE GROUP
  // ------------------------------

  const [deleteTarget, setDeleteTarget] = useState(null);

  const confirmDelete = (g) => setDeleteTarget(g);

  // --------------------------------------------------
  // 🔐 AUTORIDADE DE GESTÃO DE GRUPOS
  // --------------------------------------------------

  const isAdmin = role === "admin";
  const isGuide = role === "guide";

  const uid = user?.uid || null;
  const email = (user?.email || "").toLowerCase();

  // Resolver owner por UID
  const getOwnerUid = (g) =>
    g?.ownerId ||
    g?.owner_uid ||
    g?.ownerUid ||
    g?.createdBy ||
    g?.guideId ||
    g?.guideUid ||
    null;

  // Resolver owner por email
  const getOwnerEmail = (g) =>
    (
      g?.owner_email ||
      g?.ownerEmail ||
      g?.createdByEmail ||
      g?.guide_email ||
      g?.guideEmail ||
      ""
    ).toLowerCase();

  // Ver se o guia está explicitamente listado no grupo
  const isListedGuide = (g) => {
    const arr =
      g?.guides ||
      g?.guideUids ||
      g?.guideIds ||
      g?.admins ||
      null;

    if (!arr || !uid) return false;

    if (Array.isArray(arr)) return arr.includes(uid);
    if (typeof arr === "object") return !!arr[uid];

    return false;
  };

  // 🔑 REGRA FINAL
  const canManageGroup = (g) => {
    if (isAdmin) return true;
    if (!isGuide) return false;

    if (uid && getOwnerUid(g) === uid) return true;
    if (email && getOwnerEmail(g) === email) return true;
    if (isListedGuide(g)) return true;
    // Compatibilidade com grupos antigos: guia membro + permissão de gestão
    if (uid && g?.members?.[uid] && can("PanelGuide", "manage_group")) return true;

    return false;
  };

  const canDeleteGroup = (g) => {
    if (isAdmin) return true;
    if (!isGuide) return false;
    if (uid && getOwnerUid(g) === uid) return true;
    if (email && getOwnerEmail(g) === email) return true;
    return false;
  };

  const executeDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);

    if (!target?.id) {
      return;
    }
    if (!canDeleteGroup(target)) {
      showToast("Sem permissão para eliminar este grupo.", "warning");
      return;
    }

    try {
      await deleteGroup(target.id);
      logEvent("system", "Grupo eliminado", { gid: target.id });
      showToast("Grupo eliminado com sucesso.");
    } catch (e) {
      logEvent("error", "Erro no deleteGroup", {
        gid: target.id,
        error: e?.message,
      });
      showToast(
        `Não foi possível eliminar o grupo${
          e?.message ? `: ${String(e.message)}` : "."
        }`,
        "error"
      );
    }
  };

  // ------------------------------
  // UI
  // ------------------------------

  const renderGroupCard = (entry, suffix = "") => {
    const { group: g, event } = entry;
    if (!g?.id) return null;

    const eventInfo = event || g.eventData || null;
    const localizedEventInfo = localizeEvent(eventInfo, language);
    const localizedGroupName =
      localizedEventInfo?.name ||
      g?.translations?.[language]?.name ||
      g.name;
    const thumbnail =
      getEventImageUrl(localizedEventInfo) ||
      (g.eventData?.imageUrl ? g.eventData.imageUrl : null);
    const member = isMember(g);
    const hasUnread = isGroupUnread(g.id);
    const key = suffix ? `${g.id}-${suffix}` : g.id;
    const showGroupChat = can("PanelGuide", "group_chat");
    const showGroupSettings = can("PanelGuide", "manage_group") && canManageGroup(g);
    const showGroupDelete = canDeleteGroup(g);
    const groupActionCount = [showGroupChat, showGroupSettings, showGroupDelete].filter(Boolean).length;

    return (
      <div
        key={key}
        className={hasUnread ? "tk-event-card-v2 tk-guide-group-card has-unread" : "tk-event-card-v2 tk-guide-group-card"}
        style={{
          border: hasUnread ? "1px solid rgba(40,167,69,0.42)" : "1px solid rgba(0,0,0,0.06)",
          boxShadow: hasUnread ? "0 0 0 1px rgba(40,167,69,0.3)" : "none",
        }}
      >
        <div className={thumbnail ? "tk-event-card-v2-cover" : "tk-event-card-v2-cover is-empty"}>
          {thumbnail && (
            <img
              src={thumbnail}
              alt={localizedGroupName || translate("common.event")}
            />
          )}
          <span className="tk-guide-group-cover-status">
            <img src={base + "icons/new-message.svg"} alt="" />
            {member
              ? translate("guide.group.status.member")
              : translate("guide.group.status.visible")}
          </span>
        </div>

        <div className="tk-event-card-v2-body">
          <div className="tk-event-card-v2-heading tk-guide-group-heading">
            <div>
              <h3>
                {localizedGroupName}
                {hasUnread && <span className="tk-guide-group-unread-dot" />}
              </h3>
              <p>
                {translate("guide.group.ownerLabel")}{" "}
                {(localizedEventInfo?.guideName ||
                  localizedEventInfo?.guide?.name ||
                  localizedEventInfo?.guide?.displayName ||
                  g.owner_name ||
                  g.owner_name?.replace(/ .*/, "") ||
                  g.owner_email ||
                  translate("common.unknown"))}
              </p>
            </div>
          </div>

        {/* Ações na posição da metadata dos cartões de eventos */}
        <div
          className="tk-event-card-v2-meta tk-guide-group-actions"
          style={{
            gridTemplateColumns: "repeat(" + Math.max(1, groupActionCount) + ", minmax(0, 1fr))",
          }}
        >
          {showGroupChat && (
            <button
              type="button"
              className={`tk-guide-group-action${hasUnread ? " is-active" : ""}`}
              onClick={() => openGroupChat(g)}
            >
              <img src={`${base}icons/new-message.svg`} alt="" />
              <span>{translate("guide.actions.chat")}</span>
            </button>
          )}

          {showGroupSettings && (
            <button
              type="button"
              className="tk-guide-group-action"
              onClick={() => openGroupManage(g)}
            >
              <img src={`${base}icons/gear.svg`} alt="" />
              <span>{translate("settings.title")}</span>
            </button>
          )}

          {showGroupDelete && (
            <button
              type="button"
              className="tk-guide-group-action tk-guide-group-action-danger"
              onClick={() => confirmDelete(g)}
            >
              <img src={`${base}icons/trash.svg`} alt="" />
              <span>{translate("common.delete")}</span>
            </button>
          )}
        </div>
      </div>
      </div>
    );
  };

  return (
    <>
      <TrekkinConfirm
        visible={!!deleteTarget}
        text={translate("guide.confirm.deleteGroup", {
          name: deleteTarget?.name || translate("common.unknown"),
        })}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={executeDelete}
      />

      {aboutGuide && typeof document !== "undefined" && createPortal(
        <div
          className="tk-guide-about-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2600,
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding:
              "calc(16px + env(safe-area-inset-top)) 12px calc(16px + env(safe-area-inset-bottom))",
            boxSizing: "border-box",
          }}
        >
          <div
            className="tk-guide-about-shell"
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
            }}
            onClick={() => setAboutGuide(null)}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "480px",
              pointerEvents: "auto",
              zIndex: 1,
            }}
          >
            <div
              className="tk-guide-about-card"
              style={{
                width: "100%",
                background: "#fff",
                borderRadius: 16,
                padding: 14,
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                boxSizing: "border-box",
                maxHeight:
                  "calc(100vh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
                overflowY: "auto",
              }}
            >
              <div
                className="tk-guide-about-heading"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {translate("guide.about.title")}
                </div>
                <span className="tk-guide-about-close-wrap">
                  <CloseButton
                    onClick={() => setAboutGuide(null)}
                    ariaLabel={translate("guide.about.close")}
                  />
                </span>
              </div>

              <div
                className="tk-guide-about-description"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <div
                  className="tk-guide-about-avatar"
                  style={{
                    width: 132,
                    height: 132,
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "rgba(0,0,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    color: "#28a745",
                    fontSize: 34,
                    flexShrink: 0,
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  {(aboutGuide.photoURL || aboutGuide.avatar) ? (
                    <img
                      src={aboutGuide.photoURL || aboutGuide.avatar}
                      alt={aboutGuide.name || aboutGuide.displayName || "Guia"}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    String(
                      aboutGuide.name ||
                        aboutGuide.displayName ||
                        aboutGuide.username ||
                        "G"
                    )
                      .charAt(0)
                      .toUpperCase()
                  )}
                </div>
                <div
                  className="tk-guide-about-identity"
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#2c3e50",
                    textAlign: "center",
                  }}
                >
                  {aboutGuide.name ||
                    aboutGuide.displayName ||
                    aboutGuide.username ||
                    translate("common.unknown")}
                  <span className="tk-guide-about-role">
                    {language === "en" ? "Trekkin guide" : "Guia Trekkin"}
                  </span>
                  <span className="tk-guide-about-rating-chip">
                    <span aria-hidden="true">★</span>
                    {aboutGuideRatingSummary.count
                      ? aboutGuideRatingSummary.average.toFixed(1)
                      : (language === "en" ? "No reviews" : "Sem avaliações")}
                    {aboutGuideRatingSummary.count > 0 && (
                      <small>({aboutGuideRatingSummary.count} {aboutGuideRatingSummary.count === 1 ? (language === "en" ? "review" : "avaliação") : (language === "en" ? "reviews" : "avaliações")})</small>
                    )}
                  </span>
                </div>
              </div>

              <div
                className="tk-guide-about-bio"
                style={{
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: "#3b4b57",
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 12,
                  padding: "10px 11px",
                  whiteSpace: "pre-wrap",
                }}
              >
                <div className="tk-guide-about-bio-title">
                  {language === "en" ? "About me" : "Sobre mim"}
                </div>
                <div className="tk-guide-about-bio-text">
                  {String(
                    aboutGuide.guideDescription ||
                      aboutGuide.about ||
                      aboutGuide.bio ||
                      ""
                  ).trim() || translate("guide.about.empty")}
                </div>
              </div>

              <section className="tk-guide-about-ratings" style={{ ...guideRatingSectionStyle, marginTop: 12 }}>
                <div className="tk-guide-about-ratings-heading" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 800 }}>{language === "en" ? "Reviews" : "Avaliações"}</div>
                    <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>
                      {language === "en" ? "Experiences shared by participants." : "Experiências partilhadas por participantes."}
                    </div>
                  </div>
                  <div className="tk-guide-about-ratings-score" style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#202020" }}>
                      {aboutGuideRatingSummary.count
                        ? `${aboutGuideRatingSummary.average.toFixed(1)} ★`
                        : "—"}
                    </div>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      {aboutGuideRatingSummary.count}{" "}
                      {aboutGuideRatingSummary.count === 1 ? (language === "en" ? "review" : "avaliação") : (language === "en" ? "reviews" : "avaliações")}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                  {aboutGuideRatingSummary.reviews.length === 0 && (
                    <div style={{ color: "#777", fontSize: 12 }}>{language === "en" ? "No reviews yet." : "Ainda não existem avaliações."}</div>
                  )}
                  {aboutGuideRatingSummary.reviews
                    .slice()
                    .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0))
                    .map((review) => {
                      const savedResponse =
                        guideRatingResponses?.[aboutGuide.id]?.[review.id]?.response || "";
                      return (
                        <article className="tk-guide-about-review" key={review.id} style={guideRatingCardStyle}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                            <div style={{ fontWeight: 750, fontSize: 12 }}>
                              {review.reviewerName || "Participante"}
                            </div>
                            <div style={{ color: "#202020", fontWeight: 800, whiteSpace: "nowrap", fontSize: 12 }}>
                              {"★".repeat(clampRating(review.stars))}
                              {"☆".repeat(5 - clampRating(review.stars))}
                            </div>
                          </div>
                          <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                            {review.eventName || "Evento"}
                          </div>
                          {review.comment && (
                            <div style={{ fontSize: 12, marginTop: 7, whiteSpace: "pre-wrap" }}>
                              {review.comment}
                            </div>
                          )}
                          {savedResponse && (
                            <div className="tk-guide-about-response" style={{ marginTop: 7, padding: 8, borderRadius: 9, background: "#fff", fontSize: 11 }}>
                              <strong>Resposta do guia:</strong> {savedResponse}
                            </div>
                          )}
                        </article>
                      );
                    })}
                </div>
              </section>
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewingRequest &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Detalhes do pedido"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2250,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "calc(16px + env(safe-area-inset-top)) 10px calc(16px + env(safe-area-inset-bottom))",
              boxSizing: "border-box",
            }}
          >
            <button
              type="button"
              aria-label="Fechar detalhes"
              onClick={() => setViewingRequest(null)}
              style={{ position: "absolute", inset: 0, border: 0, background: "rgba(0,0,0,0.45)" }}
            />
            <section
              className="guide-request-modal tk-guide-request-details-modal"
              style={{
                position: "relative",
                width: "100%",
                maxWidth: 520,
                maxHeight: "min(86vh, 820px)",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                background: "#fff",
                borderRadius: 18,
                padding: 16,
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                boxSizing: "border-box",
              }}
            >
              <header className="tk-modal-standard-hero">
                <div className="tk-standard-panel-close"><CloseButton onClick={() => setViewingRequest(null)} /></div>
                <div className="tk-standard-panel-brand"><img src={`${import.meta.env.BASE_URL || "/"}icons/logo.png`} alt=""/><strong>Trekkin</strong></div>
                <h3>Detalhes do pedido</h3>
                <p>
                    {String(viewingRequest.type || "guide_online") === "trail_proposal"
                      ? "Trilho personalizado"
                      : "Guia On-Line"}
                </p>
              </header>

              {viewingRequest.proposalImageUrl && (
                <img
                  src={viewingRequest.proposalImageUrl}
                  alt="Fotografia do pedido"
                  style={{ width: "100%", maxHeight: 260, objectFit: "cover", borderRadius: 12, marginTop: 14, border: "1px solid rgba(0,0,0,0.1)" }}
                />
              )}

              <div style={{ display: "grid", gap: 10, marginTop: 14, fontSize: 13, lineHeight: 1.45 }}>
                <div><strong>Cliente:</strong> {viewingRequest.ownerName || "—"}</div>
                <div><strong>Email:</strong> {viewingRequest.ownerEmail || "—"}</div>
                <div><strong>Telemóvel:</strong> {viewingRequest.ownerPhone || "—"}</div>
                <div><strong>Data e hora:</strong> {formatDateTime(viewingRequest.startTime, language) || "—"}</div>
                <div><strong>N.º de pessoas:</strong> {viewingRequest.peopleCount || 0}</div>
                {String(viewingRequest.type || "guide_online") === "trail_proposal" ? (
                  <>
                    <div><strong>Distância pretendida:</strong> {viewingRequest.requestedKm || 0} km</div>
                    <div><strong>Dias:</strong> {viewingRequest.requestedDays || 1}</div>
                    <div><strong>Pernoita:</strong> {viewingRequest.overnightRequired ? (viewingRequest.overnightType === "lodging" ? "Alojamento" : "Campismo") : "Não"}</div>
                    <div><strong>Pessoa extra:</strong> {formatCurrency(viewingRequest?.estimate?.extraPersonPrice || 0, viewingRequest?.estimate?.currency || trailProposalConfig.currency, language)}</div>
                  </>
                ) : (
                  <div><strong>Horas pedidas pela cliente:</strong> {viewingRequest.requestedHours || 0}</div>
                )}
                {Number.isFinite(Number(viewingRequest?.pricing?.hours)) && (
                  <div>
                    <strong>Horas propostas pelo guia:</strong>{" "}
                    {Number(viewingRequest.pricing.hours)}
                  </div>
                )}
                {Number.isFinite(Number(viewingRequest?.pricing?.pricePerHour)) && (
                  <div>
                    <strong>Preço proposto por hora/pessoa:</strong>{" "}
                    {formatCurrency(
                      Number(viewingRequest.pricing.pricePerHour),
                      viewingRequest?.pricing?.currency || guideOnlineConfig.currency,
                      language
                    )}
                  </div>
                )}
                {(() => {
                  const currency =
                    viewingRequest?.pricing?.currency ||
                    viewingRequest?.estimate?.currency ||
                    guideOnlineConfig.currency;
                  const estimatedTotal = Number(viewingRequest?.estimate?.total);
                  const proposalTotalValue = [
                    viewingRequest?.pricing?.total,
                    viewingRequest?.proposalSnapshot?.proposalGroupTotalPrice,
                    viewingRequest?.acceptedEventSnapshot?.proposalGroupTotalPrice,
                    viewingRequest?.proposalSnapshot?.price,
                    viewingRequest?.acceptedEventSnapshot?.price,
                  ].find(
                    (value) =>
                      value !== null &&
                      value !== undefined &&
                      value !== "" &&
                      Number.isFinite(Number(value))
                  );
                  const hasEstimate = Number.isFinite(estimatedTotal);
                  const hasProposal = proposalTotalValue !== undefined;
                  if (!hasEstimate && !hasProposal) return null;
                  return (
                    <>
                      {hasEstimate && (
                        <div>
                          <strong>Estimativa do pedido:</strong>{" "}
                          {formatCurrency(estimatedTotal, currency, language)}
                        </div>
                      )}
                      {hasProposal && (
                        <div>
                          <strong>Valor final da proposta:</strong>{" "}
                          {Number(proposalTotalValue) <= 0
                            ? "Gratuito (oferta do guia)"
                            : formatCurrency(proposalTotalValue, currency, language)}
                        </div>
                      )}
                    </>
                  );
                })()}
                {viewingRequest.proposalWaypointName && (
                  <div><strong>Local pretendido:</strong> {viewingRequest.proposalWaypointName}</div>
                )}
                <div style={{ whiteSpace: "pre-wrap" }}>
                  <strong>Descrição do pedido:</strong><br />
                  {viewingRequest.routeDescription || "—"}
                </div>
                <div>
                  <strong>Seguro:</strong>{" "}
                  {viewingRequest.includeInsurance
                    ? `Incluído — ${formatCurrency(
                        viewingRequest?.estimate?.insurancePerPerson ??
                          guideOnlineConfig.insurancePerPerson ??
                          0,
                        viewingRequest?.estimate?.currency || guideOnlineConfig.currency,
                        language
                      )} por pessoa`
                    : "Não incluído"}
                </div>
                {String(viewingRequest.type || "guide_online") === "trail_proposal" && (
                  <div><strong>Tour todo-o-terreno:</strong> {viewingRequest.includeOffroadTour ? "Sim" : "Não"}</div>
                )}
                <div style={{ whiteSpace: "pre-wrap" }}>
                  <strong>Utilizadores incluídos:</strong><br />
                  {(() => {
                    const owner = [viewingRequest.ownerName, viewingRequest.ownerEmail]
                      .filter(Boolean)
                      .join(" — ");
                    const participants = Array.isArray(viewingRequest.guestParticipants)
                      ? viewingRequest.guestParticipants
                          .map((participant) => [participant?.name, participant?.email].filter(Boolean).join(" — "))
                          .filter(Boolean)
                      : [];
                    const emails = Array.isArray(viewingRequest.guestEmails)
                      ? viewingRequest.guestEmails
                      : parseGuestEmails(viewingRequest.guestEmails);
                    const additional = participants.length ? participants : emails;
                    return [owner, ...additional]
                      .filter(Boolean)
                      .filter((entry, index, entries) => entries.indexOf(entry) === index)
                      .join("\n") || "Sem utilizadores definidos.";
                  })()}
                </div>
                <div><strong>Estado:</strong> {GUIDE_REQUEST_STATE_LABELS[normalizeGuideRequestState(viewingRequest.status, viewingRequest)] || viewingRequest.status || "—"}</div>
                {viewingRequest.rejectedReason && (
                  <div style={{ color: "#b42318", whiteSpace: "pre-wrap" }}>
                    <strong>Motivo da recusa:</strong><br />{viewingRequest.rejectedReason}
                  </div>
                )}
              </div>

              {normalizeGuideRequestState(viewingRequest.status, viewingRequest) ===
                GUIDE_REQUEST_STATES.REQUESTED &&
                ["guide", "admin"].includes(role) && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
                    gap: 9,
                    marginTop: 18,
                    paddingTop: 14,
                    borderTop: "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      const request = viewingRequest;
                      setViewingRequest(null);
                      openRequestOwnerDirectChat(request);
                    }}
                    style={{ border: "1px solid #0d6efd", color: "#0d6efd", background: "#fff", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 750 }}
                  >
                    Direct Chat
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const request = viewingRequest;
                      setViewingRequest(null);
                      openRejectRequest(request);
                    }}
                    style={{ border: "1px solid #dc3545", color: "#dc3545", background: "#fff", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 750 }}
                  >
                    Recusar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const request = viewingRequest;
                      setViewingRequest(null);
                      openAcceptRequest(request, false);
                    }}
                    style={{ border: "1px solid #28a745", color: "#28a745", background: "#fff", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 750 }}
                  >
                    Aceitar com valor
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const request = viewingRequest;
                      setViewingRequest(null);
                      openAcceptRequest(request, true);
                    }}
                    style={{ border: "1px solid #28a745", color: "#fff", background: "#28a745", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 750 }}
                  >
                    Aceitar gratuitamente
                  </button>
                </div>
              )}
            </section>
          </div>,
          document.body
        )}

      {showOnlineRequest &&
        typeof document !== "undefined" &&
        createPortal(
        (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2100,
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding:
              "calc(16px + env(safe-area-inset-top)) 10px calc(16px + env(safe-area-inset-bottom))",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
            }}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "520px",
              minHeight: 0,
              pointerEvents: "auto",
            }}
          >
            <div
              className={"guide-request-modal " + (String(onlineRequest.requestKind || "guide_online") === "trail_proposal" ? "is-trail-proposal" : "is-guide-online")}
              onTouchMove={(event) => event.stopPropagation()}
              onWheel={(event) => event.stopPropagation()}
              style={{
                width: "100%",
                maxHeight:
                  "calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
                maxWidth: "100%",
                background: "#fff",
                borderRadius: 18,
                padding: 16,
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
                touchAction: "pan-y",
                boxSizing: "border-box",
                "--tk-guide-request-hero-image": `url(${JSON.stringify(guideRequestHeroImage)})`,
              }}
            >
              <div className="tk-guide-request-topbar">
                <button
                  type="button"
                  onClick={closeOnlineRequestModal}
                  aria-label={translate("common.cancel")}
                >
                  ‹
                </button>
                <strong>
                  {String(onlineRequest.requestKind || "guide_online") === "trail_proposal"
                    ? (language === "en" ? "Trail proposal request" : "Pedido de proposta de trilho")
                    : (language === "en" ? "Online Guide request" : "Pedido Guia On-Line")}
                </strong>
              </div>
              <div
                className="tk-guide-request-hero"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 20, lineHeight: 1.2, color: "#202020" }}>
                  {String(onlineRequest.requestKind || "guide_online") ===
                  "trail_proposal"
                    ? (language === "en" ? "Request trail proposal" : "Pedir proposta de trilho")
                    : (language === "en" ? "Request Online Guide" : "Pedir Guia On-Line")}
                </div>
                <button
                  onClick={() => {
                    closeOnlineRequestModal();
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "#666",
                  }}
                  aria-label={translate("common.cancel")}
                >
                  ×
                </button>
              </div>

              <div className="tk-guide-request-hero-subtitle" style={{ fontSize: 14, lineHeight: 1.4, color: "#5f6670", marginBottom: 14 }}>
                {String(onlineRequest.requestKind || "guide_online") ===
                "trail_proposal"
                  ? (language === "en" ? "Fill in the details to receive a private proposal for your group." : "Preencha os dados para receber uma proposta privada para o seu grupo.")
                  : translate("guide.online.form.subtitle")}
              </div>

              <div
                className="tk-guide-request-fields"
                style={{
                  display: "grid",
                  gap: 10,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              >
                <div className="tk-guide-request-section-title tk-guide-request-section-user">
                  {language === "en" ? "Your details" : "Os teus dados"}
                </div>
                <label className="tk-guide-request-name" style={{ fontSize: 13, fontWeight: 600, display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                  <span className="tk-guide-request-label"><img src={`${base}icons/user.svg`} alt="" aria-hidden="true" />{translate("guide.online.form.name")}</span>
                  <input
                    type="text"
                    value={onlineRequest.name}
                    onChange={(e) =>
                      setOnlineRequest((prev) => ({ ...prev, name: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 6,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontSize: 14,
                      boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  />
                </label>

                <label className="tk-guide-request-email" style={{ fontSize: 13, fontWeight: 600, display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                  <span className="tk-guide-request-label"><img src={`${base}icons/email.svg`} alt="" aria-hidden="true" />{translate("guide.online.form.email")}</span>
                  <input
                    type="email"
                    value={onlineRequest.email}
                    onChange={(e) =>
                      setOnlineRequest((prev) => ({ ...prev, email: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 6,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontSize: 14,
                      boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  />
                </label>

                <label className="tk-guide-request-phone" style={{ fontSize: 13, fontWeight: 600, display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                  <span className="tk-guide-request-label"><img src={`${base}icons/phone.svg`} alt="" aria-hidden="true" />{translate("guide.online.form.phone")}</span>
                  <input
                    type="tel"
                    value={onlineRequest.phone}
                    onChange={(e) =>
                      setOnlineRequest((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 6,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontSize: 14,
                      boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  />
                </label>

                <div
                  className="tk-guide-request-primary-grid"
                  style={{
                    display: "grid",
                    gap: 10,
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                  }}
                >
                  <label className="tk-guide-request-people" style={{ fontSize: 13, fontWeight: 600, display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                    <span className="tk-guide-request-label"><img src={`${base}icons/group.svg`} alt="" aria-hidden="true" />{translate("guide.online.form.peopleCount")}</span>
                    <input
                      type="number"
                      min="1"
                      value={onlineRequest.peopleCount}
                      onChange={(e) =>
                        setOnlineRequest((prev) => ({
                          ...prev,
                          peopleCount: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        marginTop: 6,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.18)",
                        fontSize: 14,
                        boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                      }}
                    />
                  </label>

                  <label className="tk-guide-request-metric" style={{ fontSize: 13, fontWeight: 600, display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                    <span className="tk-guide-request-label"><img src={`${base}icons/${String(onlineRequest.requestKind || "guide_online") === "trail_proposal" ? "distance" : "time"}.svg`} alt="" aria-hidden="true" />{String(onlineRequest.requestKind || "guide_online") === "trail_proposal"
                      ? (language === "en" ? "Intended distance (km)" : "Quilómetros pretendidos")
                      : translate("guide.online.form.requestedHours")}</span>
                    <input
                      type="number"
                      min="1"
                      step="0.5"
                      value={String(onlineRequest.requestKind || "guide_online") === "trail_proposal"
                        ? onlineRequest.requestedKm
                        : onlineRequest.requestedHours}
                      onChange={(e) =>
                        setOnlineRequest((prev) => ({
                          ...prev,
                          [String(prev.requestKind || "guide_online") === "trail_proposal"
                            ? "requestedKm"
                            : "requestedHours"]: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        marginTop: 6,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.18)",
                        fontSize: 14,
                        boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                      }}
                    />
                  </label>
                </div>

                <div className="tk-guide-request-section-title tk-guide-request-section-experience">
                  {language === "en" ? "Experience details" : "Detalhes da experiência"}
                </div>

                {String(onlineRequest.requestKind || "guide_online") === "trail_proposal" && (
                  <div className="tk-guide-request-overnight-grid" style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                    <label className="tk-guide-request-days" style={{ fontSize: 13, fontWeight: 600 }}>
                      <span className="tk-guide-request-label"><img src={`${base}icons/calendar-minus.svg`} alt="" aria-hidden="true" />{language === "en" ? "Number of days" : "Número de dias"}</span>
                      <input type="number" min="1" step="1" value={onlineRequest.requestedDays}
                        onChange={(e) => setOnlineRequest((prev) => ({ ...prev, requestedDays: e.target.value }))}
                        style={{ width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.18)", boxSizing: "border-box" }} />
                    </label>
                    <label className="tk-guide-request-overnight" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, paddingTop: 25 }}>
                      <input type="checkbox" checked={!!onlineRequest.overnightRequired}
                        onChange={(e) => setOnlineRequest((prev) => ({ ...prev, overnightRequired: e.target.checked }))} />
                      {language === "en" ? "Includes overnight stay" : "Pretende pernoita"}
                    </label>
                    {onlineRequest.overnightRequired && (
                      <label className="tk-guide-request-overnight-type" style={{ fontSize: 13, fontWeight: 600 }}>
                        {language === "en" ? "Overnight type" : "Tipo de pernoita"}
                        <select value={onlineRequest.overnightType}
                          onChange={(e) => setOnlineRequest((prev) => ({ ...prev, overnightType: e.target.value }))}
                          style={{ width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.18)", boxSizing: "border-box" }}>
                          <option value="camping">{language === "en" ? "Camping" : "Campismo"}</option>
                          <option value="lodging">{language === "en" ? "Accommodation" : "Alojamento"}</option>
                        </select>
                      </label>
                    )}
                  </div>
                )}

                <label className="tk-guide-request-date" style={{ fontSize: 13, fontWeight: 600, display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                  <span className="tk-guide-request-label"><img src={`${base}icons/calendar-plus.svg`} alt="" aria-hidden="true" />{translate("guide.online.form.startTime")}</span>
                  <span className="tk-guide-request-control tk-guide-request-date-control">
                    <input
                      type="datetime-local"
                      value={onlineRequest.startTime}
                      onChange={(e) =>
                        setOnlineRequest((prev) => ({
                          ...prev,
                          startTime: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        display: "block",
                        marginTop: 6,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.18)",
                        fontSize: 13,
                        lineHeight: 1.2,
                        boxSizing: "border-box",
                        maxWidth: "100%",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        WebkitAppearance: "none",
                      }}
                    />
                  </span>
                </label>

                <label className="tk-guide-request-description" style={{ fontSize: 13, fontWeight: 600, display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                  <span className="tk-guide-request-label"><img src={`${base}icons/details.svg`} alt="" aria-hidden="true" />{language === "en" ? "What type of route and points of interest would you like to visit?" : "Qual o tipo de percurso, e pontos de interesse gostaria de visitar?"}</span>
                  <span className="tk-guide-request-control tk-guide-request-description-control">
                    <textarea
                      value={onlineRequest.routeDescription}
                      onChange={(e) =>
                        setOnlineRequest((prev) => ({
                          ...prev,
                          routeDescription: e.target.value,
                        }))
                      }
                      rows={3}
                      style={{
                        width: "100%",
                        marginTop: 6,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.18)",
                        fontSize: 14,
                        boxSizing: "border-box",
                        maxWidth: "100%",
                        minWidth: 0,
                        resize: "vertical",
                      }}
                    />
                  </span>
                </label>
                  <div
                    className="tk-guide-request-photo"
                    style={{
                      display: "grid",
                      gap: 8,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {language === "en" ? "Request photo (optional)" : "Fotografia sobre o pedido (opcional)"}
                    </div>
                    <label
                      className="tk-guide-request-upload"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: proposalImageUploading ? "default" : "pointer",
                        background: "#f8fafc",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.1)",
                        padding: "8px 10px",
                        width: "fit-content",
                        opacity: proposalImageUploading ? 0.7 : 1,
                      }}
                    >
                      <img
                        className="tk-guide-request-upload-icon"
                        src={`${base}icons/plus.svg`}
                        alt=""
                        aria-hidden="true"
                      />
                      <span style={{ fontSize: 12, fontWeight: 600 }}>
                        {proposalImageUploading ? (language === "en" ? "Uploading image…" : "A enviar imagem…") : (language === "en" ? "Add photo" : "Adicionar fotografia")}
                      </span>
                      <input
                        type="file"
                        accept={IMAGE_ACCEPT}
                        onClick={() => {
                          window.trekkinFileType = "image";
                        }}
                        onChange={handleProposalImageUpload}
                        style={{ display: "none" }}
                        disabled={proposalImageUploading}
                      />
                    </label>
                    {onlineRequest.proposalImageUrl && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img
                          src={onlineRequest.proposalImageUrl}
                          alt=""
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 8,
                            objectFit: "cover",
                            border: "1px solid rgba(0,0,0,0.12)",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setOnlineRequest((prev) => ({ ...prev, proposalImageUrl: "" }))
                          }
                          style={{
                            border: "1px solid #dc3545",
                            color: "#dc3545",
                            background: "white",
                            borderRadius: 8,
                            padding: "5px 8px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {language === "en" ? "Remove image" : "Remover imagem"}
                        </button>
                      </div>
                    )}
                  </div>
                {String(onlineRequest.requestKind || "guide_online") ===
                  "trail_proposal" && (
                  <div
                    className="tk-guide-request-experience"
                    style={{
                      display: "grid",
                      gap: 8,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    <div>{language === "en" ? "Experience type" : "Tipo de experiência"}</div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="trail_proposal_mode"
                        checked={!onlineRequest.includeOffroadTour}
                        onChange={() =>
                          setOnlineRequest((prev) => ({
                            ...prev,
                            includeOffroadTour: false,
                          }))
                        }
                      />
                      {language === "en" ? "Hiking" : "Caminhada"}
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="trail_proposal_mode"
                        checked={!!onlineRequest.includeOffroadTour}
                        onChange={() =>
                          setOnlineRequest((prev) => ({
                            ...prev,
                            includeOffroadTour: true,
                          }))
                        }
                      />
                      {language === "en" ? "Hiking + 4x4" : "Caminhada + 4x4"}
                    </label>
                  </div>
                )}

                <div
                  className="tk-guide-request-participants"
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div className="tk-guide-request-participants-title">
                    {language === "en" ? "Participants (name and email)" : "Participantes (nome e email)"}
                  </div>
                  {(onlineRequest.guestParticipants || []).map((p, idx) => (
                    <div
                      key={`gp-${idx}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr auto",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={p?.name || ""}
                        onChange={(e) =>
                          setOnlineRequest((prev) => {
                            const arr = Array.isArray(prev.guestParticipants)
                              ? [...prev.guestParticipants]
                              : [];
                            arr[idx] = { ...(arr[idx] || {}), name: e.target.value };
                            return { ...prev, guestParticipants: arr };
                          })
                        }
                        placeholder={language === "en" ? "Name" : "Nome"}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid rgba(0,0,0,0.18)",
                          fontSize: 13,
                          boxSizing: "border-box",
                        }}
                      />
                      <input
                        type="email"
                        value={p?.email || ""}
                        onChange={(e) =>
                          setOnlineRequest((prev) => {
                            const arr = Array.isArray(prev.guestParticipants)
                              ? [...prev.guestParticipants]
                              : [];
                            arr[idx] = { ...(arr[idx] || {}), email: e.target.value };
                            return { ...prev, guestParticipants: arr };
                          })
                        }
                        placeholder="Email"
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid rgba(0,0,0,0.18)",
                          fontSize: 13,
                          boxSizing: "border-box",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setOnlineRequest((prev) => {
                            const arr = Array.isArray(prev.guestParticipants)
                              ? [...prev.guestParticipants]
                              : [];
                            arr.splice(idx, 1);
                            return { ...prev, guestParticipants: arr };
                          })
                        }
                        style={{
                          border: "1px solid #dc3545",
                          color: "#dc3545",
                          background: "white",
                          borderRadius: 8,
                          padding: "6px 8px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {language === "en" ? "Remove" : "Remover"}
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setOnlineRequest((prev) => ({
                        ...prev,
                        guestParticipants: [
                          ...(Array.isArray(prev.guestParticipants)
                            ? prev.guestParticipants
                            : []),
                          { name: "", email: "" },
                        ],
                      }))
                    }
                    style={{
                      alignSelf: "flex-start",
                      border: "1px dashed rgba(0,0,0,0.2)",
                      color: "#333",
                      background: "#f5f5f5",
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {language === "en" ? "Add participant" : "Adicionar participante"}
                  </button>
                </div>
              </div>

              <div
                className="tk-guide-request-actions"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 10,
                  marginTop: 16,
                }}
              >
                <button
                  className="tk-guide-request-cancel"
                  onClick={() => {
                    closeOnlineRequestModal();
                  }}
                  style={{
                    border: "1px solid #ccc",
                    background: "#fff",
                    color: "#555",
                    borderRadius: 10,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <img src="./icons/close.svg" alt="" aria-hidden="true" />
                  <span>{translate("common.cancel")}</span>
                  <b aria-hidden="true">›</b>
            </button>
            <button
                  className="tk-guide-request-submit"
                  onClick={handleSubmitOnlineRequest}
                  disabled={onlineSubmitting}
                  style={{
                    border: "none",
                    background: "#28a745",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: onlineSubmitting ? "default" : "pointer",
                    opacity: onlineSubmitting ? 0.7 : 1,
                  }}
                >
                  <img src="./icons/send.svg" alt="" aria-hidden="true" />
                  <span>{onlineSubmitting
                    ? translate("guide.online.form.submitting")
                    : translate("guide.online.form.submit")}</span>
                  <b aria-hidden="true">›</b>
                </button>
              </div>
            </div>
          </div>
        </div>
        ),
        document.body
      )}

      {acceptingRequest &&
        typeof document !== "undefined" &&
        createPortal(
        (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2100,
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding:
              "calc(16px + env(safe-area-inset-top)) 10px calc(16px + env(safe-area-inset-bottom))",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
            }}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "520px",
              maxHeight:
                "calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
              pointerEvents: "auto",
            }}
          >
            <div
              className="tk-guide-featured-preview"
              style={{
                width: "100%",
                maxWidth: "100%",
                background: "#fff",
                borderRadius: 18,
                padding: 16,
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                maxHeight:
                  "calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  {translate("guide.online.accept.title")}
                </div>
                <button
                  onClick={() => setAcceptingRequest(null)}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "#666",
                  }}
                  aria-label={translate("common.cancel")}
                >
                  ×
                </button>
              </div>

              <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
                {acceptingRequest.ownerName || translate("common.user")} •{" "}
                {acceptingRequest.ownerEmail}
              </div>

              <div
                style={{
                  background: "rgba(40,167,69,0.08)",
                  borderRadius: 12,
                  padding: "10px 12px",
                  border: "1px solid rgba(40,167,69,0.2)",
                  marginBottom: 12,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                  {translate("guide.online.details.title")}
                </div>
                <div style={{ fontSize: 12, color: "#555", display: "grid", gap: 6 }}>
                  <div>
                    <strong>{translate("guide.online.form.phone")}:</strong>{" "}
                    {acceptingRequest.ownerPhone ||
                      translate("guide.online.details.none")}
                  </div>
                  <div>
                    <strong>{translate("guide.online.form.startTime")}:</strong>{" "}
                    {formatDateTime(acceptingRequest.startTime, language) ||
                      translate("guide.online.details.none")}
                  </div>
                  <div>
                    <strong>{translate("guide.online.form.peopleCount")}:</strong>{" "}
                    {acceptingRequest.peopleCount || 0}
                  </div>
                  {String(acceptingRequest?.type || "guide_online") === "trail_proposal" ? (
                    <>
                      <div><strong>Distância:</strong> {acceptingRequest.requestedKm || 0} km</div>
                      <div><strong>Dias:</strong> {acceptingRequest.requestedDays || 1}</div>
                      <div><strong>Pernoita:</strong> {acceptingRequest.overnightRequired ? (acceptingRequest.overnightType === "lodging" ? "Alojamento" : "Campismo") : "Não"}</div>
                      <div><strong>Valor por pessoa extra:</strong> {formatCurrency(acceptingRequest?.estimate?.extraPersonPrice || 0, acceptingRequest?.estimate?.currency || trailProposalConfig.currency, language)}</div>
                    </>
                  ) : (
                    <div><strong>{translate("guide.online.form.requestedHours")}:</strong> {acceptingRequest.requestedHours || 0}</div>
                  )}
                  <div>
                    <strong>
                      {translate("guide.online.form.routeDescription")}:
                    </strong>{" "}
                    {acceptingRequest.routeDescription ||
                      translate("guide.online.details.none")}
                  </div>
                  {acceptingRequest?.proposalImageUrl && (
                    <div>
                      <strong>Fotografia do pedido:</strong>
                      <div style={{ marginTop: 6 }}>
                        <img
                          src={acceptingRequest.proposalImageUrl}
                          alt="Imagem de referência"
                          style={{
                            width: "100%",
                            maxWidth: 240,
                            borderRadius: 8,
                            objectFit: "cover",
                            border: "1px solid rgba(0,0,0,0.12)",
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <strong>{translate("guide.online.form.guestEmails")}:</strong>{" "}
                    {(() => {
                      const emails = Array.isArray(acceptingRequest.guestEmails)
                        ? acceptingRequest.guestEmails
                        : parseGuestEmails(acceptingRequest.guestEmails);
                      return emails.length
                        ? emails.join(", ")
                        : translate("guide.online.details.none");
                    })()}
                  </div>
                  {String(acceptingRequest?.type || "guide_online") ===
                    "trail_proposal" && (
                    <div>
                      <strong>Tour veículo todo o terreno:</strong>{" "}
                      {acceptingRequest.includeOffroadTour ? "Sim" : "Não"}
                    </div>
                  )}
                  <div>
                    <strong>
                      {translate("guide.online.form.includeInsurance")}:
                    </strong>{" "}
                    {acceptingRequest.includeInsurance
                      ? translate("guide.online.details.insuranceIncluded")
                      : translate("guide.online.details.insuranceExcluded")}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>
                  {translate("guide.online.accept.hours")}
                  <input
                    type="number"
                    min="1"
                    step="0.5"
                    value={acceptForm.hours}
                    onChange={(e) =>
                      setAcceptForm((prev) => ({ ...prev, hours: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 6,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontSize: 14,
                      boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  />
                </label>

                <label style={{ fontSize: 13, fontWeight: 600 }}>
                  {translate("guide.online.accept.pricePerHour")}
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={acceptForm.pricePerHour}
                    onChange={(e) =>
                      setAcceptForm((prev) => ({
                        ...prev,
                        pricePerHour: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 6,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontSize: 14,
                      boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  />
                </label>

                <label style={{ fontSize: 13, fontWeight: 600 }}>
                  {translate("guide.online.accept.insurancePerPerson")}
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={acceptForm.insurancePerPerson}
                    onChange={(e) =>
                      setAcceptForm((prev) => ({
                        ...prev,
                        insurancePerPerson: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      marginTop: 6,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontSize: 14,
                      boxSizing: "border-box",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  />
                </label>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={acceptForm.offer}
                    onChange={(e) =>
                      setAcceptForm((prev) => ({
                        ...prev,
                        offer: e.target.checked,
                      }))
                    }
                  />
                  {translate("guide.online.accept.offer")}
                </label>
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "rgba(40,167,69,0.08)",
                  border: "1px solid rgba(40,167,69,0.2)",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: "#28a745" }}>
                  {translate("guide.online.accept.total")}:{" "}
                  {formatCurrency(
                    acceptTotal,
                    guideOnlineConfig.currency,
                    language
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: 16,
                }}
              >
                <button
                  onClick={() => setAcceptingRequest(null)}
                  style={{
                    border: "1px solid #ccc",
                    background: "#fff",
                    color: "#555",
                    borderRadius: 10,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {translate("common.cancel")}
                </button>
                <button
                  onClick={handleAcceptRequest}
                  disabled={!!acceptingRequest._saving}
                  style={{
                    border: "none",
                    background: "#28a745",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: acceptingRequest._saving ? "default" : "pointer",
                    opacity: acceptingRequest._saving ? 0.7 : 1,
                  }}
                >
                  {acceptingRequest._saving
                    ? translate("guide.online.accept.saving")
                    : translate("guide.online.accept.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
        ),
        document.body
      )}

      {rejectingRequest &&
        typeof document !== "undefined" &&
        createPortal(
        (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2100,
            pointerEvents: "auto",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: "10px",
              right: "10px",
              top: "calc(20px + env(safe-area-inset-top))",
              bottom: "calc(80px + env(safe-area-inset-bottom))",
              maxWidth: "520px",
              margin: "0 auto",
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                maxWidth: "100%",
                background: "#fff",
                borderRadius: 18,
                padding: 16,
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                maxHeight: "100%",
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  {translate("guide.online.reject.title")}
                </div>
                <button
                  onClick={() => setRejectingRequest(null)}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "#666",
                  }}
                  aria-label={translate("common.cancel")}
                >
                  ×
                </button>
              </div>

              <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
                {rejectingRequest.ownerName || translate("common.user")} •{" "}
                {rejectingRequest.ownerEmail}
              </div>

              <label style={{ fontSize: 13, fontWeight: 600 }}>
                {translate("guide.online.reject.reason")}
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={translate("guide.online.reject.placeholder")}
                  rows={5}
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(0,0,0,0.18)",
                    fontSize: 14,
                    boxSizing: "border-box",
                    maxWidth: "100%",
                    minWidth: 0,
                    resize: "vertical",
                  }}
                />
              </label>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: 16,
                }}
              >
                <button
                  onClick={() => setRejectingRequest(null)}
                  style={{
                    border: "1px solid #ccc",
                    background: "#fff",
                    color: "#555",
                    borderRadius: 10,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {translate("common.cancel")}
                </button>
                <button
                  onClick={handleRejectRequest}
                  disabled={!!rejectingRequest._saving}
                  style={{
                    border: "none",
                    background: "#dc3545",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: rejectingRequest._saving ? "default" : "pointer",
                    opacity: rejectingRequest._saving ? 0.7 : 1,
                  }}
                >
                  {rejectingRequest._saving
                    ? translate("guide.online.reject.saving")
                    : translate("guide.online.reject.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
        ),
        document.body
      )}

      <div
        className="tk-guides-panel-v2"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          color: "#333",
          position: "relative",
          WebkitUserSelect: "none",
          userSelect: "none",
          touchAction: "manipulation",
        }}
      >
        {/* Fechar */}
        <div className="tk-guides-panel-close tk-standard-panel-close">
          <CloseButton onClick={() => resetToMap()} />
        </div>

        <header className="tk-guides-header-v2">
          <div className="tk-guides-header-brand">
            <img src={`${base}icons/logo.png`} alt="" />
            <span>Trekkin</span>
          </div>
          <h2>{language === "en" ? "Guides" : "Guias"}</h2>
          <p>{translate("guide.availableGuides.title")}</p>
        </header>

        {availableGuides.length > 0 && (
          <div
            className="tk-guides-directory-v2"
            style={{
              marginTop: 12,
              marginBottom: 14,
            }}
          >
            <div
              className="tk-guides-directory-heading"
              style={{
                width: "100%",
                color: "#28a745",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              <span>{language === "en" ? "Available guides" : "Guias disponíveis"}</span>
              {availableGuides.length > 3 && (
                <button type="button" onClick={() => setShowAllGuides((value) => !value)}>
                  {showAllGuides
                    ? (language === "en" ? "Show less" : "Ver menos")
                    : (language === "en" ? "View all" : "Ver todos")}
                  <b aria-hidden="true">›</b>
                </button>
              )}
            </div>
            <div
              className="tk-guide-public-grid"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                marginTop: 10,
              }}
            >
              {availableGuides
                .slice(0, showAllGuides ? undefined : 3)
                .map((g) => {
                const label =
                  g.name || g.displayName || g.username || translate("common.unknown");
                const photo = g.photoURL || g.avatar || null;
                return (
                  <div
                    className="tk-guide-public-card"
                    key={g.id}
                    style={{
                      flex: "0 0 calc((100% - 24px) / 3)",
                      maxWidth: "calc((100% - 24px) / 3)",
                      boxSizing: "border-box",
                      minWidth: 0,
                      background: "rgba(255,255,255,0.9)",
                      borderRadius: 14,
                      padding: "10px 8px",
                      border: "1px solid rgba(0,0,0,0.06)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        overflow: "hidden",
                        background: "rgba(0,0,0,0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        color: "#28a745",
                        flexShrink: 0,
                        position: "relative",
                      }}
                    >
                      <span aria-hidden="true">{String(label).charAt(0).toUpperCase()}</span>
                      {photo ? (
                        <img
                          src={photo}
                          alt={label}
                          onError={(event) => { event.currentTarget.style.display = "none"; }}
                          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : null}
                    </div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 12,
                        textAlign: "center",
                        lineHeight: 1.1,
                        maxWidth: 100,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={label}
                    >
                      {label}
                    </div>
                    {(() => {
                      const summary = ratingSummaryByGuide.get(String(g.id));
                      return summary?.count > 0 ? (
                        <div className="tk-guide-public-rating" style={{ fontSize: 11, fontWeight: 700, color: "#202020" }}>
                          <span className="tk-guide-public-rating-star" aria-hidden="true">★</span>{" "}
                          <span>{language === "en"
                            ? summary.average.toFixed(1)
                            : summary.average.toFixed(1).replace(".", ",")} ({summary.count} {summary.count === 1
                            ? (language === "en" ? "review" : "avaliação")
                            : (language === "en" ? "reviews" : "avaliações")})</span>
                        </div>
                      ) : (
                        <div className="tk-guide-public-rating" style={{ fontSize: 10, color: "#777" }}>
                          {translate("guide.ratings.noPublicScore")}
                        </div>
                      );
                    })()}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        alignItems: "stretch",
                        width: "100%",
                        marginTop: "auto",
                      }}
                    >
                      <button
                        className="tk-guide-waypoint-action"
                        onClick={() => openGuideAbout(g)}
                        style={{
                          border: "1px solid #28a745",
                          color: "#28a745",
                          background: "white",
                          borderRadius: 10,
                          padding: "4px 8px",
                          fontSize: 11,
                          fontWeight: 600,
                          width: "100%",
                        }}
                      >
                        <img src={`${base}icons/user.svg`} alt="" />
                        <span>{translate("guide.actions.about")}</span>
                        <b aria-hidden="true">›</b>
                      </button>
                      {canDirectChat && <button
                        className="tk-guide-waypoint-action"
                        onClick={() => openGuideChat(g)}
                        style={{
                          border: "1px solid #28a745",
                          color: "#28a745",
                          background: "white",
                          borderRadius: 10,
                          padding: "4px 8px",
                          fontSize: 11,
                          fontWeight: 600,
                          width: "100%",
                        }}
                      >
                        <img src={`${base}icons/new-message.svg`} alt="" />
                        <span>{translate("guide.actions.chat")}</span>
                        <b aria-hidden="true">›</b>
                      </button>}
                    </div>
                  </div>
                );
              })}
            </div>
            {pendingGuideRatings.length > 0 && (
              <section className="tk-guide-pending-ratings" style={{ ...guideRatingSectionStyle, marginTop: 14 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Avaliar um guia</div>
                <div style={{ color: "#666", fontSize: 12, marginTop: 3 }}>
                  Partilha a tua experiência depois de um evento concluído.
                </div>
                <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                  {pendingGuideRatings.map((target) => (
                    <div key={target.key} style={{ ...guideRatingCardStyle, display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", alignItems: "center", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 750, fontSize: 13 }}>{target.guideName}</div>
                        <div style={{ color: "#666", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{target.eventName}</div>
                      </div>
                      <button type="button" onClick={() => openGuideRating(target)} style={guideRatingPrimaryButtonStyle}>
                        Avaliar
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {["guide", "admin"].includes(role) && (
          <section className="tk-guide-own-ratings-v2" style={guideRatingSectionStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{translate("guide.ratings.latest")}</div>
                <div style={{ color: "#666", fontSize: 12, marginTop: 3 }}>
                  {translate("guide.ratings.confirmHint")}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#202020" }}>
                  {latestOwnGuideRatings.length}
                </div>
                <div style={{ fontSize: 11, color: "#666" }}>
                  {latestOwnGuideRatings.length === 1 ? translate("guide.ratings.newSingle") : translate("guide.ratings.newPlural")}
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
              {latestOwnGuideRatings.length === 0 && (
                <div style={{ color: "#777", fontSize: 13 }}>{translate("guide.ratings.noneNew")}</div>
              )}
              {latestOwnGuideRatings
                .map((review) => {
                  const savedResponse = guideRatingResponses?.[user.uid]?.[review.id]?.response || "";
                  return (
                    <article key={review.id} style={guideRatingCardStyle}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontWeight: 750, fontSize: 13 }}>{review.reviewerName || "Participante"}</div>
                        <div style={{ color: "#202020", fontWeight: 800, whiteSpace: "nowrap" }}>
                          {"★".repeat(clampRating(review.stars))}{"☆".repeat(5 - clampRating(review.stars))}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{review.eventName || "Evento"}</div>
                      {review.comment && <div style={{ fontSize: 13, marginTop: 8, whiteSpace: "pre-wrap" }}>{review.comment}</div>}
                      {savedResponse ? (
                        <div style={{ marginTop: 8, padding: 8, borderRadius: 9, background: "#fff", fontSize: 12 }}>
                          <strong>Resposta do guia:</strong> {savedResponse}
                        </div>
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 7, marginTop: 8 }}>
                          <input
                            value={responseDrafts[review.id] || ""}
                            onChange={(event) => setResponseDrafts((current) => ({ ...current, [review.id]: event.target.value }))}
                            placeholder="Responder ao participante..."
                            style={guideRatingInputStyle}
                          />
                          <button
                            type="button"
                            onClick={() => saveGuideResponse(review)}
                            disabled={savingResponseId === review.id}
                            style={guideRatingPrimaryButtonStyle}
                          >
                            Responder
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => confirmGuideRating(review)}
                        disabled={confirmingRatingId === review.id}
                        style={{ ...guideRatingPrimaryButtonStyle, width: "100%", marginTop: 8 }}
                      >
                        {confirmingRatingId === review.id ? "A confirmar…" : "Confirmar"}
                      </button>
                    </article>
                  );
                })}
            </div>
          </section>
        )}

        {ratingTarget && typeof document !== "undefined" && createPortal(
          <div style={guideRatingModalBackdropStyle} onClick={() => setRatingTarget(null)}>
            <div style={guideRatingModalStyle} onClick={(event) => event.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800 }}>Avaliar guia</div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                    {ratingTarget.guideName} · {ratingTarget.eventName}
                  </div>
                </div>
                <CloseButton onClick={() => setRatingTarget(null)} />
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 7, margin: "20px 0" }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingDraft(star)}
                    aria-label={`${star} estrelas`}
                    style={{ border: "none", background: "transparent", color: star <= ratingDraft ? "#f4b400" : "#b8bdc5", fontSize: 38, lineHeight: 1, padding: 2, cursor: "pointer" }}
                  >
                    ★
                  </button>
                ))}
              </div>
              <textarea
                value={ratingComment}
                onChange={(event) => setRatingComment(event.target.value)}
                maxLength={1000}
                rows={5}
                placeholder="Comentário opcional sobre a experiência..."
                style={{ ...guideRatingInputStyle, resize: "vertical", minHeight: 110 }}
              />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
                <button type="button" onClick={() => setRatingTarget(null)} style={guideRatingSecondaryButtonStyle}>Cancelar</button>
                <button type="button" onClick={saveGuideRating} disabled={savingGuideRating} style={guideRatingPrimaryButtonStyle}>
                  {savingGuideRating ? "A guardar..." : "Guardar avaliação"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        <div className="tk-guide-request-grid-v2">
        {/* Guia On-Line */}
        <button
          type="button"
          className="tk-guide-request-card-v2 tk-guide-request-card-online"
          onClick={() => {
            setOnlineRequest((prev) => ({
              ...prev,
              requestKind: "guide_online",
            }));
            openOnlineRequest();
          }}
        >
          <strong>{language === "en" ? "Request Online Guide" : "Pedir Guia On-Line"}</strong>
          <span>{language === "en" ? "Request remote guidance for your adventure." : "Solicita acompanhamento remoto para a tua aventura."}</span>
          <b aria-hidden="true">›</b>
        </button>

        {/* Proposta de Trilho */}
        <button
          type="button"
          className="tk-guide-request-card-v2 tk-guide-request-card-trail"
          onClick={() => {
            setOnlineRequest((prev) => ({
              ...prev,
              requestKind: "trail_proposal",
            }));
            openOnlineRequest();
          }}
        >
          <strong>{language === "en" ? "Request trail proposal" : "Pedir proposta de trilho"}</strong>
          <span>{language === "en" ? "Receive a private proposal tailored to your group." : "Recebe uma proposta privada feita à medida do teu grupo."}</span>
          <b aria-hidden="true">›</b>
        </button>
        </div>

        <>
        {featuredWaypoints.length > 0 && (
          <div
            className="tk-guide-showcase"
            style={{
              background: "rgba(255,255,255,0.9)",
              borderRadius: 16,
              padding: "12px 12px",
              border: "1px solid rgba(0,0,0,0.06)",
              marginBottom: 8,
            }}
          >
            <button
              type="button"
              className="tk-guide-showcase-toggle"
              aria-expanded={showFeaturedWaypoints}
              onClick={() => setShowFeaturedWaypoints((value) => !value)}
            >
              <img src={featuredWaypoints[0]?.imageUrl} alt="" />
              <span>
                <strong>{translate("guide.featured.title")}</strong>
                <small>{language === "en" ? "Discover trails and remarkable destinations" : "Descobre trilhos e destinos incríveis"}</small>
              </span>
              <b aria-hidden="true">{showFeaturedWaypoints ? "⌃" : "›"}</b>
            </button>
            {showFeaturedWaypoints && <div className="tk-guide-showcase-content" style={{ display: "grid", gridTemplateColumns: "24px 1fr 24px", gap: 8, alignItems: "center" }}>
              <button
                type="button"
                onClick={() => setFeaturedSlide((s) => Math.max(0, s - 1))}
                disabled={featuredSlide === 0}
                style={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "white",
                  borderRadius: 8,
                  height: 28,
                  cursor: featuredSlide === 0 ? "default" : "pointer",
                  opacity: featuredSlide === 0 ? 0.4 : 1,
                }}
              >
                ‹
              </button>
              <div className="tk-guide-showcase-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                {featuredWaypoints
                  .slice(featuredSlide * 4, featuredSlide * 4 + 4)
                  .map((wp) => (
                    <div
                      key={`featured-${wp.id}`}
                      style={{
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderRadius: 10,
                        overflow: "hidden",
                        background: "#fff",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <img
                        src={wp.imageUrl}
                        alt={wp.name || "Waypoint"}
                        style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        onClick={() => setFeaturedPreview(wp)}
                        style={{
                          border: "none",
                          borderTop: "1px solid rgba(0,0,0,0.08)",
                          background: "white",
                          color: "#1f2937",
                          fontWeight: 700,
                          fontSize: 12,
                          padding: "8px 6px",
                          cursor: "pointer",
                        }}
                      >
                        {translate("guide.partnerAccommodation.view")}
                      </button>
                    </div>
                  ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  setFeaturedSlide((s) =>
                    Math.min(Math.max(0, Math.ceil(featuredWaypoints.length / 4) - 1), s + 1)
                  )
                }
                disabled={featuredSlide >= Math.max(0, Math.ceil(featuredWaypoints.length / 4) - 1)}
                style={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "white",
                  borderRadius: 8,
                  height: 28,
                  cursor:
                    featuredSlide >= Math.max(0, Math.ceil(featuredWaypoints.length / 4) - 1)
                      ? "default"
                      : "pointer",
                  opacity:
                    featuredSlide >= Math.max(0, Math.ceil(featuredWaypoints.length / 4) - 1)
                      ? 0.4
                      : 1,
                }}
              >
                ›
              </button>
            </div>}
          </div>
        )}
        {partnerWaypoints.length > 0 && (
          <div className="tk-guide-showcase" style={{background:"rgba(255,255,255,0.9)",borderRadius:16,padding:12,border:"1px solid rgba(0,0,0,0.06)",marginBottom:8}}>
            <button
              type="button"
              className="tk-guide-showcase-toggle"
              aria-expanded={showPartnerWaypoints}
              onClick={() => setShowPartnerWaypoints((value) => !value)}
            >
              <img src={partnerWaypoints[0]?.imageUrl} alt="" />
              <span>
                <strong>{translate("guide.partnerAccommodation.title")}</strong>
                <small>{language === "en" ? "Stay close to your next adventure" : "Fica perto da tua próxima aventura"}</small>
              </span>
              <b aria-hidden="true">{showPartnerWaypoints ? "⌃" : "›"}</b>
            </button>
            {showPartnerWaypoints && <div className="tk-guide-showcase-content" style={{display:"grid",gridTemplateColumns:"24px 1fr 24px",gap:8,alignItems:"center"}}>
              <button type="button" disabled={partnerSlide===0} onClick={()=>setPartnerSlide((s)=>Math.max(0,s-1))} style={{height:28,border:"1px solid #ddd",borderRadius:8,background:"white"}}>‹</button>
              <div className="tk-guide-showcase-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:8}}>{partnerWaypoints.slice(partnerSlide*4,partnerSlide*4+4).map((wp)=><div key={`partner-${wp.id}`} style={{border:"1px solid rgba(0,0,0,.08)",borderRadius:10,overflow:"hidden",background:"white"}}><img src={wp.imageUrl} alt={wp.name || translate("guide.partnerAccommodation.item")} style={{display:"block",width:"100%",aspectRatio:"1 / 1",objectFit:"cover"}}/><button type="button" onClick={()=>{
                if (wp.isBrandGroup) { setPartnerBrandPreview(wp); return; }
                const target = wp.waypoints?.[0] || wp;
                resetToMap();
                window.setTimeout(() => window.dispatchEvent(new CustomEvent("trekkin:focus-waypoint", { detail: { waypointId: target.id, waypointName: target.name, lat: target.lat, lon: target.lon, imageUrl: target.imageUrl, description: target.desc || target.description, tel: target.tel, email: target.email, www: target.www, icon: target.icon, zoom: 16, exclusive: true, source: "partner_recommendation", waypoint: target } })), 120);
              }} style={{width:"100%",border:0,borderTop:"1px solid #eee",background:"white",fontWeight:700,fontSize:12,padding:"8px 4px",cursor:"pointer"}}>{translate("guide.partnerAccommodation.view")}</button></div>)}</div>
              <button type="button" disabled={partnerSlide>=Math.max(0,Math.ceil(partnerWaypoints.length/4)-1)} onClick={()=>setPartnerSlide((s)=>Math.min(Math.max(0,Math.ceil(partnerWaypoints.length/4)-1),s+1))} style={{height:28,border:"1px solid #ddd",borderRadius:8,background:"white"}}>›</button>
            </div>}
          </div>
        )}
        {partnerBrandPreview && <div style={{background:"rgba(255,255,255,.98)",border:"1px solid rgba(0,0,0,.1)",borderRadius:16,padding:12,marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:8}}><strong>{partnerBrandPreview.name}</strong><button type="button" onClick={()=>setPartnerBrandPreview(null)} style={{border:0,background:"transparent",fontSize:22}}>×</button></div>
          <div style={{display:"grid",gap:7}}>{(partnerBrandPreview.waypoints || []).map((wp)=><button key={wp.id} type="button" onClick={()=>{ setPartnerBrandPreview(null); resetToMap(); window.setTimeout(() => window.dispatchEvent(new CustomEvent("trekkin:focus-waypoint", { detail: { waypointId: wp.id, waypointName: wp.name, lat: wp.lat, lon: wp.lon, imageUrl: wp.imageUrl, description: wp.desc || wp.description, tel: wp.tel, email: wp.email, www: wp.www, icon: wp.icon, zoom:16, exclusive:true, source:"partner_recommendation", waypoint:wp } })),120); }} style={{display:"grid",gridTemplateColumns:"50px 1fr auto",alignItems:"center",gap:9,textAlign:"left",padding:7,border:"1px solid #e5e7eb",borderRadius:10,background:"white"}}><img src={wp.imageUrl} alt="" style={{width:50,height:50,objectFit:"cover",borderRadius:8}}/><b>{wp.name || translate("guide.partnerAccommodation.item")}</b><span>›</span></button>)}</div>
        </div>}
        {activityMode === "bike" && bicycleAssistanceWaypoints.length > 0 && (
          <div className="tk-guide-showcase tk-guide-bike-showcase">
            <button
              type="button"
              className="tk-guide-showcase-toggle tk-guide-bike-showcase-heading"
              aria-expanded={showBicycleAssistance}
              onClick={() => setShowBicycleAssistance((value) => !value)}
            >
              <span className="tk-menu-icon-tile" aria-hidden="true"><img src={`${base}icons/bicycle.svg`} alt="" /></span>
              <span><strong>{translate("guide.bicycleAssistance.title")}</strong><small>{language === "en" ? "Assistance and rentals for your next ride" : "Assistência e aluguer para a tua próxima aventura"}</small></span>
              <b aria-hidden="true">{showBicycleAssistance ? "⌃" : "›"}</b>
            </button>
            {showBicycleAssistance && <div className="tk-guide-showcase-content" style={{display:"grid",gridTemplateColumns:"24px 1fr 24px",gap:8,alignItems:"center"}}>
              <button type="button" disabled={bicycleAssistanceSlide===0} onClick={()=>setBicycleAssistanceSlide((s)=>Math.max(0,s-1))} style={{height:28,border:"1px solid #ddd",borderRadius:8,background:"white"}}>‹</button>
              <div className="tk-guide-showcase-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:8}}>
                {bicycleAssistanceWaypoints.slice(bicycleAssistanceSlide*4,bicycleAssistanceSlide*4+4).map((wp)=><div key={`bike-assistance-${wp.id}`} style={{border:"1px solid rgba(0,0,0,.08)",borderRadius:10,overflow:"hidden",background:"white"}}>
                  <img src={wp.imageUrl || `${base}icons/gear.svg`} alt={wp.name || translate("guide.bicycleAssistance.item")} style={{width:"100%",aspectRatio:"1 / 1",objectFit:wp.imageUrl?"cover":"contain",padding:wp.imageUrl?0:12,boxSizing:"border-box"}}/>
                  <button type="button" onClick={()=>{
                    resetToMap();
                    window.setTimeout(() => window.dispatchEvent(new CustomEvent("trekkin:focus-waypoint", { detail: { waypointId: wp.id, waypointName: wp.name, lat: wp.lat, lon: wp.lon, imageUrl: wp.imageUrl, description: wp.desc || wp.description, tel: wp.tel, email: wp.email, www: wp.www, icon: wp.icon, zoom: 16, exclusive: true, source: "bicycle_assistance", waypoint: wp } })), 120);
                  }} style={{width:"100%",border:0,borderTop:"1px solid #eee",background:"white",fontWeight:700,fontSize:12,padding:"8px 4px",cursor:"pointer"}}>{translate("guide.bicycleAssistance.view")}</button>
                </div>)}
              </div>
              <button type="button" disabled={bicycleAssistanceSlide>=Math.max(0,Math.ceil(bicycleAssistanceWaypoints.length/4)-1)} onClick={()=>setBicycleAssistanceSlide((s)=>Math.min(Math.max(0,Math.ceil(bicycleAssistanceWaypoints.length/4)-1),s+1))} style={{height:28,border:"1px solid #ddd",borderRadius:8,background:"white"}}>›</button>
            </div>}
          </div>
        )}
        {activityMode === "bike" && bicycleAssistanceWaypoints.some((wp) => String(wp?.partnerUserUid || "") === String(user?.uid || "")) && (
          <button
            type="button"
            className="tk-guide-menu-button tk-guide-bike-management-button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openPanel("bikeService", { mode: "partner" });
            }}
          >
            <span className="tk-menu-icon-tile" aria-hidden="true"><img src={`${base}icons/guide.svg`} alt="" /></span>
            <span><strong>{language === "en" ? "Manage service sheets" : "Gerir fichas de serviço"}</strong><small>{language === "en" ? "Review assistance requests" : "Consulta os pedidos de assistência"}</small></span>
            <b aria-hidden="true">›</b>
          </button>
        )}
        {activityMode === "bike" && bicycleAssistanceWaypoints.some((wp) => wp?.rentalPartner === true && String(wp?.partnerUserUid || "") === String(user?.uid || "")) && (
          <button
            type="button"
            className="tk-guide-menu-button tk-guide-bike-management-button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openPanel("bikeRental", { mode: "partner" });
            }}
          >
            <span className="tk-menu-icon-tile" aria-hidden="true"><img src={`${base}icons/bicycle.svg`} alt="" /></span>
            <span><strong>{language === "en" ? "Manage bicycle rentals" : "Gerir aluguer de bicicletas"}</strong><small>{language === "en" ? "Manage your catalogue and requests" : "Gere o catálogo e os pedidos"}</small></span>
            <b aria-hidden="true">›</b>
          </button>
        )}
        </>
        {featuredPreview && createPortal((
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => setFeaturedPreview(null)}
          >
            <div
              className="tk-guide-featured-preview"
              style={{
                width: "min(720px, 95vw)",
                maxHeight: "90vh",
                background: "#fff",
                borderRadius: 14,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "12px 14px",
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#1f2937",
                }}
              >
                {featuredPreview.name || "Waypoint"}
              </div>
              <img
                src={featuredPreview.imageUrl}
                alt={featuredPreview.name || "Waypoint"}
                style={{ width: "100%", maxHeight: "58vh", objectFit: "cover" }}
              />
              <div style={{ padding: "12px 14px", fontSize: 14, color: "#333", whiteSpace: "pre-wrap" }}>
                {featuredPreview.desc || featuredPreview.name || (language === "en" ? "No description available." : "Sem descrição disponível.")}
              </div>
              <div style={{ padding: "0 14px 14px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setFeaturedPreview(null)}
                  style={{
                    border: "1px solid rgba(0,0,0,0.2)",
                    background: "white",
                    color: "#222",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {language === "en" ? "Close" : "Fechar"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const selected = featuredPreview;
                    setFeaturedPreview(null);
                    openTrailProposalFromWaypoint(selected, { deferOpenMs: 180 });
                  }}
                  style={{
                    border: "none",
                    background: "#28a745",
                    color: "white",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {language === "en" ? "Request proposal" : "Pedir proposta"}
                </button>
              </div>
            </div>
          </div>
        ), document.body)}

        <button
          type="button"
            className="tk-guide-menu-button tk-guide-my-requests-button"
          aria-expanded={showMyRequests}
          onClick={() => setShowMyRequests((value) => !value)}
        >
          <svg className="tk-guide-menu-button-icon tk-guide-request-inline-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <path d="M8 3.5h12l5 5V27H8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M20 3.5V9h5M12 14h9M12 18h9M12 22h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>
            <strong>{language === "en" ? "My requests" : "Os meus pedidos"}</strong>
            <small>{language === "en" ? "Track the status of your requests" : "Acompanha o estado dos teus pedidos"}</small>
          </span>
          <b aria-hidden="true">{showMyRequests ? "⌃" : "›"}</b>
        </button>

        {showMyRequests && <>
        {userGuideOnlineRequests.length === 0 && userTrailProposalRequests.length === 0 && (
          <div className="tk-guide-menu-empty">
            {language === "en" ? "You have no requests yet." : "Ainda não tens pedidos."}
          </div>
        )}
        {userGuideOnlineRequests.length > 0 && (
          <div
            style={{
              background: "rgba(255,255,255,0.9)",
              borderRadius: 16,
              padding: "12px 12px",
              border: "1px solid rgba(0,0,0,0.06)",
              marginBottom: 8,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
              {translate("guide.online.myRequests")}
            </div>
            {userGuideOnlineRequests.map((req) => {
              const status = normalizeGuideRequestState(req.status, req);
              const canDeleteRequest = [
                GUIDE_REQUEST_STATES.REQUESTED,
                GUIDE_REQUEST_STATES.REJECTED,
              ].includes(status);
              const requestEventId = String(req?.eventId || "").trim();
              const requestEventName = String(req?.eventName || "").trim();
              const requestEvent = requestEventId ? eventsById.get(requestEventId) : null;
              const paymentStatus =
                req.paymentStatus || req.payment?.status || null;
              const isOffer =
                req?.pricing?.offer === true || paymentStatus === "not_required";
              const isPaid = paymentStatus === "paid";
              const statusLabel = GUIDE_REQUEST_STATE_LABELS[status] || status;
              const statusColor = [GUIDE_REQUEST_STATES.CONFIRMED_PRIVATE, GUIDE_REQUEST_STATES.PAID].includes(status)
                ? "#28a745"
                : status === GUIDE_REQUEST_STATES.REJECTED
                ? "#dc3545"
                : "#9a6700";
              const requestGroup = req.groupId
                ? groupsById.get(req.groupId)
                : null;
              return (
                <div
                  key={req.id}
                  style={{
                    padding: "8px 0",
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {requestEventName ||
                          requestEvent?.name ||
                          `${String(req.type || "guide_online") === "trail_proposal"
                            ? (language === "en" ? "Trail proposal" : "Proposta de trilho")
                            : "Guia On-Line"}${
                            req.proposalWaypointName
                              ? ` • ${req.proposalWaypointName}`
                              : ""
                          }`}
                      </div>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {req.peopleCount}{" "}
                        {translate("guide.online.labels.people")} •{" "}
                        {String(req.type || "guide_online") === "trail_proposal"
                          ? `${req.requestedKm || 0} km • ${req.requestedDays || 1} ${language === "en" ? "day(s)" : "dia(s)"}${req.overnightRequired ? ` • ${req.overnightType === "lodging" ? (language === "en" ? "Accommodation" : "Alojamento") : (language === "en" ? "Camping" : "Campismo")}` : ""}`
                          : `${req.requestedHours} ${translate("guide.online.labels.hours")}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: statusColor }}>
                        {statusLabel}
                      </div>
                      {![GUIDE_REQUEST_STATES.REQUESTED, GUIDE_REQUEST_STATES.REJECTED].includes(status) && (
                        <>
                          {isOffer && (
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#28a745",
                                border: "1px solid rgba(40,167,69,0.4)",
                                padding: "2px 6px",
                                borderRadius: 8,
                              }}
                            >
                              {translate("guide.online.payment.free")}
                            </div>
                          )}
                          {isPaid && (
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#28a745",
                                border: "1px solid rgba(40,167,69,0.4)",
                                padding: "2px 6px",
                                borderRadius: 8,
                              }}
                            >
                              {translate("guide.online.payment.paid")}
                            </div>
                          )}
                        </>
                      )}
                      {canDeleteRequest && (
                        <TrekkinButton
                          icon="trash.svg"
                          square
                          onClick={() => handleDeleteOnlineRequest(req)}
                        />
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 7 }}>
                    <button
                      type="button"
                      className="tk-customer-proposal-action"
                      onClick={() => setViewingRequest(req)}
                      style={customerProposalViewButtonStyle}
                    >
                      {language === "en" ? "View request" : "Ver pedido"}
                    </button>
                    {requestEventId && ![
                      GUIDE_REQUEST_STATES.REQUESTED,
                      GUIDE_REQUEST_STATES.GUIDE_ASSIGNED,
                      GUIDE_REQUEST_STATES.PROPOSAL_DRAFT,
                      GUIDE_REQUEST_STATES.REJECTED,
                    ].includes(status) && (
                      <button
                        type="button"
                        className="tk-customer-proposal-action is-positive"
                        onClick={() => handleViewCustomerProposal(req, requestEventId)}
                        style={{ ...customerProposalViewButtonStyle, color: "#28a745", borderColor: "#28a745" }}
                      >
                        Ver proposta
                      </button>
                    )}
                  </div>

                  {status === GUIDE_REQUEST_STATES.AWAITING_CUSTOMER && requestEventId && (
                    <>
                    {guideProposalDiffersFromRequest(req) && Number(req.proposalViewedVersion || 0) !== Number(req.proposalVersion || 1) && (
                      <div style={{ marginTop: 7, fontSize: 11, color: "#9a6700", textAlign: "right" }}>
                        Vê primeiro a proposta atual para poderes aceitar.
                      </div>
                    )}
                    <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 7 }}>
                      <button type="button" className="tk-customer-proposal-action" onClick={() => handleCustomerProposalAction(req, "request_changes")} style={customerProposalViewButtonStyle}>
                        Pedir alterações
                      </button>
                      <button type="button" className="tk-customer-proposal-action is-danger" onClick={() => handleCustomerProposalAction(req, "reject")} style={{ ...customerProposalViewButtonStyle, color: "#b42318", borderColor: "#b42318" }}>
                        Rejeitar
                      </button>
                      <button
                        type="button"
                        disabled={payingRequestId === req.id || (guideProposalDiffersFromRequest(req) && Number(req.proposalViewedVersion || 0) !== Number(req.proposalVersion || 1))}
                        onClick={() => handleCustomerProposalAction(req, "accept")}
                        style={{ ...customerProposalButtonStyle, opacity: payingRequestId === req.id || (guideProposalDiffersFromRequest(req) && Number(req.proposalViewedVersion || 0) !== Number(req.proposalVersion || 1)) ? 0.5 : 1 }}
                      >
                        {payingRequestId === req.id ? "A processar…" : "Aceitar"}
                      </button>
                    </div>
                    </>
                  )}
                  {[
                    GUIDE_REQUEST_STATES.CUSTOMER_ACCEPTED,
                    GUIDE_REQUEST_STATES.PAYMENT_PENDING,
                    GUIDE_REQUEST_STATES.PAYMENT_FAILED,
                  ].includes(status) && !isOffer && !isPaid && (
                    <div style={{ marginTop: 8 }}>
                      <button type="button" disabled={payingRequestId === req.id} onClick={() => handlePayOnlineRequest(req)} style={guideRatingPrimaryButtonStyle}>
                        {payingRequestId === req.id ? "A preparar…" : "Receber link de pagamento"}
                      </button>
                    </div>
                  )}

                  {status === "rejected" && req.rejectedReason && (
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: "#dc3545",
                        lineHeight: 1.4,
                      }}
                    >
                      {translate("guide.online.reject.reasonLabel")}:{" "}
                      {req.rejectedReason}
                    </div>
                  )}

                  {requestGroup && (
                    <div
                      style={{
                        marginTop: 8,
                        background: "rgba(255,255,255,0.9)",
                        borderRadius: 12,
                        padding: "8px 10px",
                        border: "1px solid rgba(0,0,0,0.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {requestGroup.name || translate("guide.group.descriptionFallback")}
                        </div>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          {translate("guide.online.group.label")}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {can("PanelGuide", "group_chat") && isMember(requestGroup) && (
                          <TrekkinButton
                            icon="new-message.svg"
                            square
                            onClick={() => openGroupChat(requestGroup)}
                          />
                        )}
                        {can("PanelGuide", "manage_group") &&
                          canManageGroup(requestGroup) && (
                            <TrekkinButton
                              icon="gear.svg"
                              square
                              onClick={() => openGroupManage(requestGroup)}
                            />
                          )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {userTrailProposalRequests.length > 0 && (
          <div
            style={{
              background: "rgba(255,255,255,0.9)",
              borderRadius: 16,
              padding: "12px 12px",
              border: "1px solid rgba(0,0,0,0.06)",
              marginBottom: 8,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
              {language === "en" ? "Trail proposal requests" : "Pedidos de proposta de trilho"}
            </div>
            {userTrailProposalRequests.map((req) => {
              const status = normalizeGuideRequestState(req.status, req);
              const canDeleteRequest = [
                GUIDE_REQUEST_STATES.REQUESTED,
                GUIDE_REQUEST_STATES.REJECTED,
              ].includes(status);
              const requestEventId = String(req?.eventId || "").trim();
              const requestEventName = String(req?.eventName || "").trim();
              const requestEvent = requestEventId ? eventsById.get(requestEventId) : null;
              const paymentStatus = req.paymentStatus || req.payment?.status || null;
              const isOffer = req?.pricing?.offer === true || paymentStatus === "not_required";
              const isPaid = paymentStatus === "paid";
              const statusLabel = GUIDE_REQUEST_STATE_LABELS[status] || status;
              const statusColor = [GUIDE_REQUEST_STATES.CONFIRMED_PRIVATE, GUIDE_REQUEST_STATES.PAID].includes(status)
                ? "#28a745"
                : status === GUIDE_REQUEST_STATES.REJECTED
                ? "#dc3545"
                : "#9a6700";
              return (
                <div key={req.id} style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {requestEventName ||
                          requestEvent?.name ||
                          `${language === "en" ? "Trail proposal" : "Proposta de trilho"}${
                            req.proposalWaypointName
                              ? ` • ${req.proposalWaypointName}`
                              : ""
                          }`}
                      </div>
                      <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                        Data/hora: {formatDateTime(req.startTime, language) || "—"}
                      </div>
                      <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                        Participantes: {req.peopleCount || 0}
                      </div>
                      <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                        Modalidade: {req.includeOffroadTour ? "Caminhada + 4x4" : "Caminhada"}
                      </div>
                      <div style={{ fontSize: 12, color: "#444", marginTop: 4, whiteSpace: "pre-wrap" }}>
                        {req.routeDescription || "Sem descrição do percurso."}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: statusColor }}>{statusLabel}</div>
                      {canDeleteRequest && (
                        <TrekkinButton icon="trash.svg" square onClick={() => handleDeleteOnlineRequest(req)} />
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 7 }}>
                    <button type="button" className="tk-customer-proposal-action" onClick={() => setViewingRequest(req)} style={customerProposalViewButtonStyle}>
                      {language === "en" ? "View request" : "Ver pedido"}
                    </button>
                    {requestEventId && ![GUIDE_REQUEST_STATES.REQUESTED, GUIDE_REQUEST_STATES.REJECTED].includes(status) && (
                      <button
                        type="button"
                        className="tk-customer-proposal-action is-positive"
                        onClick={() => handleViewCustomerProposal(req, requestEventId)}
                        style={{ ...customerProposalViewButtonStyle, color: "#28a745", borderColor: "#28a745" }}
                      >
                        Ver proposta
                      </button>
                    )}
                  </div>

                  {status === GUIDE_REQUEST_STATES.AWAITING_CUSTOMER && requestEventId && (
                    <>
                    {guideProposalDiffersFromRequest(req) && Number(req.proposalViewedVersion || 0) !== Number(req.proposalVersion || 1) && (
                      <div style={{ marginTop: 7, fontSize: 11, color: "#9a6700", textAlign: "right" }}>
                        Vê primeiro a proposta atual para poderes aceitar.
                      </div>
                    )}
                    <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 7 }}>
                      <button type="button" className="tk-customer-proposal-action" onClick={() => handleCustomerProposalAction(req, "request_changes")} style={customerProposalViewButtonStyle}>
                        Pedir alterações
                      </button>
                      <button type="button" className="tk-customer-proposal-action is-danger" onClick={() => handleCustomerProposalAction(req, "reject")} style={{ ...customerProposalViewButtonStyle, color: "#b42318", borderColor: "#b42318" }}>
                        Rejeitar
                      </button>
                      <button
                        type="button"
                        disabled={payingRequestId === req.id || (guideProposalDiffersFromRequest(req) && Number(req.proposalViewedVersion || 0) !== Number(req.proposalVersion || 1))}
                        onClick={() => handleCustomerProposalAction(req, "accept")}
                        style={{ ...customerProposalButtonStyle, opacity: payingRequestId === req.id || (guideProposalDiffersFromRequest(req) && Number(req.proposalViewedVersion || 0) !== Number(req.proposalVersion || 1)) ? 0.5 : 1 }}
                      >
                        {payingRequestId === req.id ? "A processar…" : "Aceitar"}
                      </button>
                    </div>
                    </>
                  )}
                  {[
                    GUIDE_REQUEST_STATES.CUSTOMER_ACCEPTED,
                    GUIDE_REQUEST_STATES.PAYMENT_PENDING,
                    GUIDE_REQUEST_STATES.PAYMENT_FAILED,
                  ].includes(status) && !isOffer && !isPaid && (
                    <div style={{ marginTop: 8 }}>
                      <button type="button" disabled={payingRequestId === req.id} onClick={() => handlePayOnlineRequest(req)} style={guideRatingPrimaryButtonStyle}>
                        {payingRequestId === req.id ? "A preparar…" : "Receber link de pagamento"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>}

        {["guide", "admin"].includes(role) && (
          <details className="tk-guide-pending-accordion">
            <summary>
              <span className="tk-guide-pending-icon tk-guide-pending-icon-online" aria-hidden="true" />
              <span>
                <strong>{translate("guide.pending.onlineTitle")}</strong>
                <small>{pendingGuideOnlineRequests.length} {language === "en" ? "pending" : "pendente(s)"}</small>
              </span>
              <b aria-hidden="true">›</b>
            </summary>
            <div className="tk-guide-pending-accordion-content">
            {pendingGuideOnlineRequests.length === 0 && (
              <div style={{ fontSize: 12, color: "#777" }}>
                {translate("guide.online.pending.empty")}
              </div>
            )}
            {pendingGuideOnlineRequests.map((req) => (
              <div
                key={req.id}
                style={{
                  padding: "8px 0",
                  borderTop: "1px solid rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {req.ownerName || translate("common.user")}
                  </div>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    {String(req.type || "guide_online") === "trail_proposal"
                      ? (language === "en" ? "Trail proposal" : "Proposta de trilho")
                      : (language === "en" ? "Online Guide" : "Guia On-Line")}{" "}
                    •{" "}
                    {req.peopleCount} {translate("guide.online.labels.people")} •{" "}
                    {String(req.type || "guide_online") === "trail_proposal"
                      ? `${req.requestedKm || 0} km • ${req.requestedDays || 1} ${language === "en" ? "day(s)" : "dia(s)"}${req.overnightRequired ? ` • ${req.overnightType === "lodging" ? (language === "en" ? "Accommodation" : "Alojamento") : (language === "en" ? "Camping" : "Campismo")}` : ""}`
                      : `${req.requestedHours} ${translate("guide.online.labels.hours")}`}
                  </div>
                  {req.proposalWaypointName && (
                    <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
                      Waypoint pedido: {req.proposalWaypointName}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => setViewingRequest(req)}
                    style={{
                      border: "1px solid #202020",
                      color: "#202020",
                      background: "white",
                      borderRadius: 10,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {language === "en" ? "View request" : "Ver pedido"}
                  </button>
                </div>
              </div>
            ))}
            </div>
          </details>
        )}

        {["guide", "admin"].includes(role) &&
          acceptedManagedTrailProposals.length > 0 && (
            <div
              style={{
                background: "rgba(255,255,255,0.9)",
                borderRadius: 16,
                padding: "12px 12px",
                border: "1px solid rgba(0,0,0,0.06)",
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                Propostas em curso
              </div>
              {acceptedManagedTrailProposals.map((req) => {
                const requestEventId = String(req?.eventId || "").trim();
                const requestEventName = String(req?.eventName || "").trim();
                const requestEvent = requestEventId ? eventsById.get(requestEventId) : null;
                const hasLinkedProposalEvent = !!requestEventId;
                const requestState = normalizeGuideRequestState(req.status, req);
                return (
                  <div
                    key={`managed-${req.id}`}
                    style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.06)" }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {requestEventName ||
                        requestEvent?.name ||
                        `${String(req?.type || "guide_online") === "trail_proposal"
                          ? (language === "en" ? "Trail proposal" : "Proposta de trilho")
                          : "Guia On-Line"}${
                          req.proposalWaypointName
                            ? ` • ${req.proposalWaypointName}`
                            : ""
                        }`}
                    </div>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                      {req.peopleCount || 0} {translate("guide.online.labels.people")}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#2c6b3f", marginTop: 2 }}>
                      {GUIDE_REQUEST_STATE_LABELS[requestState] || requestState}
                    </div>
                    {requestState === GUIDE_REQUEST_STATES.CHANGES_REQUESTED && req.changesRequested && (
                      <div style={{ fontSize: 12, color: "#8a5a00", marginTop: 5, whiteSpace: "pre-wrap" }}>
                        Alterações pedidas: {req.changesRequested}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                      Modalidade: {req.includeOffroadTour ? "Caminhada + 4x4" : "Caminhada"}
                    </div>
                    {req.proposalImageUrl && (
                      <div style={{ fontSize: 12, color: "#0d6efd", marginTop: 2 }}>
                        Inclui imagem de referência
                      </div>
                    )}
                    <div
                      className="tk-proposal-actions"
                      style={{
                        width: "100%",
                        marginTop: 8,
                        display: "grid",
                        gap: 8,
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setViewingRequest(req)}
                        style={{
                          border: "1px solid #202020",
                          color: "#202020",
                          background: "white",
                          borderRadius: 8,
                          padding: "5px 9px",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {language === "en" ? "View request" : "Ver pedido"}
                      </button>
                      {(role === "guide" ||
                        role === "admin" ||
                        can("PanelGuide", "direct_chat")) && (
                        <button
                          type="button"
                          onClick={() => openRequestOwnerDirectChat(req)}
                          style={{
                            border: "1px solid #0d6efd",
                            color: "#0d6efd",
                            background: "white",
                            borderRadius: 8,
                            padding: "5px 9px",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Direct Chat
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          hasLinkedProposalEvent
                            ? openPanel("eventEdit", { id: requestEventId, eventId: requestEventId })
                            : openAcceptRequest(req, null, true)
                        }
                        style={{
                          border: "1px solid #28a745",
                          color: "#fff",
                          background: "#28a745",
                          borderRadius: 8,
                          padding: "5px 9px",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {hasLinkedProposalEvent
                          ? (language === "en" ? "Edit proposal" : "Editar proposta")
                          : (language === "en" ? "Create proposal" : "Criar proposta")}
                      </button>
                      {String(req?.type || "guide_online") === "guide_online" &&
                        [
                          GUIDE_REQUEST_STATES.AWAITING_CUSTOMER,
                          GUIDE_REQUEST_STATES.CHANGES_REQUESTED,
                        ].includes(requestState) && (
                        <button
                          type="button"
                          onClick={() => openAcceptRequest(req)}
                          style={{
                            border: "1px solid #202020",
                            color: "#202020",
                            background: "white",
                            borderRadius: 8,
                            padding: "5px 9px",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Alterar condições
                        </button>
                      )}
                      {requestState === GUIDE_REQUEST_STATES.CHANGES_REQUESTED && hasLinkedProposalEvent && (
                        <>
                          <button
                            type="button"
                            onClick={() => openPanel("eventEdit", { id: requestEventId, eventId: requestEventId })}
                            style={{ border: "1px solid #202020", color: "#202020", background: "white", borderRadius: 8, padding: "5px 9px", fontSize: 11, fontWeight: 700 }}
                          >
                            {language === "en" ? "Edit proposal" : "Editar proposta"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResubmitProposal(req)}
                            style={{ border: "1px solid #28a745", color: "#fff", background: "#28a745", borderRadius: 8, padding: "5px 9px", fontSize: 11, fontWeight: 700 }}
                          >
                            {language === "en" ? "Resubmit proposal" : "Reenviar proposta"}
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleNotifyProposalReady(req)}
                        disabled={notifyingRequestId === req.id}
                        style={{
                          border: "1px solid #28a745",
                          color: "#28a745",
                          background: "white",
                          borderRadius: 8,
                          padding: "5px 9px",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: notifyingRequestId === req.id ? "default" : "pointer",
                          opacity: notifyingRequestId === req.id ? 0.7 : 1,
                        }}
                      >
                        {notifyingRequestId === req.id ? "A notificar..." : "Notificar cliente"}
                      </button>
                      {hasLinkedProposalEvent && (
                        <button
                          type="button"
                          onClick={() => openPanel("eventDetails", { eventId: requestEventId })}
                          style={{
                            border: "1px solid #28a745",
                            color: "#28a745",
                            background: "white",
                            borderRadius: 8,
                            padding: "5px 9px",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Ver evento
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteOnlineRequest(req)}
                        style={{
                          border: "1px solid #dc3545",
                          color: "#dc3545",
                          background: "white",
                          borderRadius: 8,
                          padding: "5px 9px",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Eliminar tudo
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        {["guide", "admin"].includes(role) && (
          <details className="tk-guide-pending-accordion">
            <summary>
              <span className="tk-guide-pending-icon tk-guide-pending-icon-trail" aria-hidden="true" />
              <span>
                <strong>{translate("guide.pending.trailTitle")}</strong>
                <small>{pendingTrailProposalRequests.length} {language === "en" ? "pending" : "pendente(s)"}</small>
              </span>
              <b aria-hidden="true">›</b>
            </summary>
            <div className="tk-guide-pending-accordion-content">
            {pendingTrailProposalRequests.length === 0 && (
              <div style={{ fontSize: 12, color: "#777" }}>{translate("guide.online.pending.empty")}</div>
            )}
            {pendingTrailProposalRequests.map((req) => (
              <div
                key={req.id}
                style={{
                  padding: "8px 0",
                  borderTop: "1px solid rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {req.ownerName || translate("common.user")}
                  </div>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    {language === "en" ? "Trail proposal" : "Proposta de trilho"}
                  </div>
                  {req.proposalWaypointName && (
                    <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
                      {language === "en" ? "Requested waypoint" : "Waypoint pedido"}: {req.proposalWaypointName}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                    {language === "en" ? "Date/time" : "Data/hora"}: {formatDateTime(req.startTime, language) || "—"} • {req.peopleCount || 0} {language === "en" ? "participants" : "participantes"}
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                    {language === "en" ? "Activity" : "Modalidade"}: {req.includeOffroadTour ? (language === "en" ? "Hiking + 4x4" : "Caminhada + 4x4") : (language === "en" ? "Hiking" : "Caminhada")}
                  </div>
                  {req.proposalImageUrl && (
                    <div style={{ fontSize: 12, color: "#0d6efd", marginTop: 2 }}>
                      {language === "en" ? "Includes reference image" : "Inclui imagem de referência"}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#444", marginTop: 4, whiteSpace: "pre-wrap" }}>
                    {req.routeDescription || (language === "en" ? "No route description." : "Sem descrição do percurso.")}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => setViewingRequest(req)}
                    style={{
                      border: "1px solid #202020",
                      color: "#202020",
                      background: "white",
                      borderRadius: 10,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {language === "en" ? "View request" : "Ver pedido"}
                  </button>
                </div>
              </div>
            ))}
            </div>
          </details>
        )}

        {/* Histórico de chats diretos do utilizador ou do guia */}
        {can("PanelGuide", "direct_chat") && (
          <>
            <button
              type="button"
              className="tk-guide-menu-button"
              aria-expanded={showDirectChatHistory}
              onClick={() => setShowDirectChatHistory((value) => !value)}
            >
              <span className="tk-menu-icon-tile tk-direct-chat-history-icon" aria-hidden="true"><img src={`${base}guide-online-thumbnail.png`} alt="" /></span>
              <span>
                <strong>{language === "en" ? "Direct chat history" : "Histórico de chats diretos"}</strong>
                <small>{language === "en" ? "Review your conversations with guides" : "Consulta as tuas conversas com guias"}</small>
              </span>
              <b aria-hidden="true">{showDirectChatHistory ? "⌃" : "›"}</b>
            </button>
            {showDirectChatHistory && (
            <div
              className="tk-guide-direct-history-v2"
              style={{
                background: "rgba(255,255,255,0.9)",
                borderRadius: 16,
                padding: 14,
                border: "1px solid rgba(0,0,0,0.07)",
                margin: "16px 0",
                boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  margin: "0 0 12px",
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#1f2937",
                }}
              >
                <img
                  src={`${base}icons/new-message.svg`}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
                {language === "en" ? "Direct chat history" : "Histórico de chats diretos"}
              </div>
              {directChats.length === 0 && (
                <div
                  style={{
                    fontSize: 13,
                    color: "#777",
                    padding: "11px 12px",
                    borderRadius: 12,
                    background: "rgba(0,0,0,0.035)",
                  }}
                >
                  {translate("guide.direct.empty")}
                </div>
              )}

              {directChats.map((c) => (
                <div
                  key={c.chatId}
                  style={{
                    background: "rgba(40,167,69,0.045)",
                    borderRadius: 12,
                    padding: 11,
                    border: "1px solid rgba(40,167,69,0.16)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    {/* Avatar */}
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        overflow: "hidden",
                        background: "rgba(0,0,0,0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        color: "#28a745",
                        flexShrink: 0,
                      }}
                    >
                      {c.contactPhoto ? (
                        <img
                          src={c.contactPhoto}
                          alt={c.contactName || "Utilizador"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        String(c.contactName || "U").charAt(0).toUpperCase()
                      )}
                    </div>

                    {/* Texto */}
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {c.contactName || "Utilizador"}
                        {c.unread === true && (
                          <span
                            style={{
                              display: "inline-block",
                              marginLeft: 6,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#28a745",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {translate("guide.direct.label")}
                      </div>
                    </div>
                  </div>

                  <TrekkinButton
                    icon="new-message.svg"
                    variant="primary"
                    onClick={() => openDirectChat(c)}
                    style={{
                      flex: "0 0 auto",
                      minWidth: 132,
                      minHeight: 44,
                      padding: "10px 14px",
                      gap: 8,
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 800,
                    }}
                  >
                    {language === "en" ? "Open chat" : "Abrir chat"}
                  </TrekkinButton>
                </div>
              ))}
            </div>
            )}
          </>
        )}

        <div className="tk-guide-community-grid-v2">
          <a
            className="tk-guide-whatsapp-button"
            href="https://chat.whatsapp.com/IY4bOOsSD7j0enpObji8td"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="tk-guide-whatsapp-icon" aria-hidden="true">
              <img src={`${base}icons/whatsapp.svg`} alt="" />
            </span>
            <span className="tk-guide-whatsapp-copy">
              <strong>{translate("guide.actions.whatsappGroup")}</strong>
              <small>{translate("guide.actions.whatsappCommunityHint")}</small>
            </span>
            <b aria-hidden="true">›</b>
          </a>
          <div className="tk-guide-newsletter-card">
            <div className="tk-guide-newsletter-copy">
              <strong>{translate("guide.newsletter.title")}</strong>
              <span>{translate("guide.newsletter.description")}</span>
            </div>
            <a
              className="tk-guide-newsletter-button"
              href="https://eepurl.com/iR9iDg"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={`${base}icons/new-message.svg`} alt="" />
              <span>{translate("guide.newsletter.subscribe")}</span>
              <b aria-hidden="true">›</b>
            </a>
          </div>
        </div>

        {/* Criar Grupo */}
        {can("PanelGuide", "create_group") && (
          <div
            style={{
              background: "rgba(255,255,255,0.85)",
              borderRadius: 16,
              padding: "12px 12px",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>
              {translate("guide.createGroup.title")}
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <input
                type="text"
                placeholder={translate("guide.createGroup.placeholder")}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.18)",
                  fontSize: 14,
                }}
              />

              <TrekkinButton
                icon="adduser.svg"
                onClick={handleCreateGroup}
                square
              />
            </div>
          </div>
        )}

        {groupsWithMeta.length === 0 && (
          <p style={{ color: "#666", fontSize: 14 }}>
            {translate("guide.groups.empty")}
          </p>
        )}

        {futureGroups.length > 0 && (
          <h4 style={{ margin: "12px 0 6px", color: "#444", fontWeight: 600 }}>
            {language === "en" ? "Groups for upcoming events" : "Grupos dos próximos eventos"}
          </h4>
        )}

        {futureGroups.map((entry) => renderGroupCard(entry))}

        {pastGroups.length > 0 && (
          <h4 style={{ margin: "18px 0 6px", color: "#666", fontWeight: 600 }}>
            {translate("guide.groups.past")}
          </h4>
        )}

        {pastGroups.map((entry) => renderGroupCard(entry, "past"))}

      </div>
    </>
  );
}
